import React, { useEffect, useState, useRef } from "react";
import PageHeader from "shared/components/PageHeader/PageHeader";
import JSZip from "jszip";
import {
  Calendar,
  HelpCircle,
  Code,
  CheckSquare,
  Plus,
  Search,
  Edit,
  Trash2,
  Clock,
  Users,
  X,
  Save,
  Eye,
  Upload,
  Download,
  AlertCircle,
  Image,
  FileText,
  ArrowUp,
  ArrowDown,
} from "react-feather";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  where,
} from "firebase/firestore";
import { db, storage, auth } from "shared/utils/firebase";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import styles from "./TestManagement.module.css";

const sanitizeHtml = (html) => {
  if (!html) return "";
  return String(html)
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
    .replace(/on[a-zA-Z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "")
    .replace(/href\s*=\s*["']?\s*javascript:[^"'>]*/gi, 'href="#"')
    .replace(/src\s*=\s*["']?\s*javascript:[^"'>]*/gi, 'src=""');
};

function TestManagement() {
  const [activeSubTab, setActiveSubTab] = useState("addCodeQuestions");

  // Refs for custom image insertion
  const testQuestionTextareaRef = useRef(null);
  const codeQuestionTextareaRef = useRef(null);

  // Image Upload States for Quiz Questions
  const [uploadedQuestionImages, setUploadedQuestionImages] = useState([]);
  const [questionImageFile, setQuestionImageFile] = useState(null);
  const [uploadingQuestionImage, setUploadingQuestionImage] = useState(false);
  const [questionImageProgress, setQuestionImageProgress] = useState(0);
  const [questionImageError, setQuestionImageError] = useState("");
  const [questionImageSuccess, setQuestionImageSuccess] = useState(false);

  // Image Upload States for Code Questions
  const [uploadedCodeImages, setUploadedCodeImages] = useState([]);
  const [codeImageFile, setCodeImageFile] = useState(null);
  const [uploadingCodeImage, setUploadingCodeImage] = useState(false);
  const [codeImageProgress, setCodeImageProgress] = useState(0);
  const [codeImageError, setCodeImageError] = useState("");
  const [codeImageSuccess, setCodeImageSuccess] = useState(false);

  // Question ZIP Bundle Import States
  const [showQuestionZipImport, setShowQuestionZipImport] = useState(false);
  const [questionZipFile, setQuestionZipFile] = useState(null);
  const [questionZipStatus, setQuestionZipStatus] = useState({ message: "", type: "", details: [] });
  const [questionZipProgress, setQuestionZipProgress] = useState({ phase: "", current: 0, total: 0 });
  const [questionZipImporting, setQuestionZipImporting] = useState(false);

  // Tests (scheduling)
  const [tests, setTests] = useState([]);
  const [filteredTests, setFilteredTests] = useState([]);
  const [testSearch, setTestSearch] = useState("");
  const [testStatusFilter, setTestStatusFilter] = useState("all");
  const [testCategoryFilter, setTestCategoryFilter] = useState("all");
  const [testTypeFilter, setTestTypeFilter] = useState("all");
  const [showTestModal, setShowTestModal] = useState(false);
  const [editingTest, setEditingTest] = useState(null);
  const [showViewTestModal, setShowViewTestModal] = useState(false);
  const [viewingTest, setViewingTest] = useState(null);
  const [testQuestions, setTestQuestions] = useState([]);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [showCSVUpload, setShowCSVUpload] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvUploadStatus, setCsvUploadStatus] = useState({ message: "", type: "" });
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [testQuestionForm, setTestQuestionForm] = useState({
    questionText: "",
    optionA: "",
    optionB: "",
    optionC: "",
    optionD: "",
    correctOption: "A",
    marks: 1,
    explanation: "",
    imageUrl: "",
  });

  const categories = [
    "VLSI Design",
    "Circuit Design",
    "AI Tech",
    "Embedded/IoT",
    "Signal Processing",
    "Antenna",
    "Mobile Communication",
    "Basic Electronics",
    "Communications & Networking",
    "Artificial Intelligence",
    "Projects",
    "Programming Languages",
    "General Tech"
  ];

  const codingLanguages = [
    "Verilog",
    "SystemVerilog",
    "Vhdl",
    "Matlab",
    "Assembly",
    "C",
    "Cpp",
    "Python"
  ];

  const [testForm, setTestForm] = useState({
    title: "",
    description: "",
    courseName: "",
    category: "",
    type: "quiz",
    date: "",
    time: "",
    duration: "",
    totalMarks: "",
    passMarks: "",
    attemptsAllowed: 1,
    targetGroup: "",
    isProctored: false,
    isPublished: false,
    initialCode: "",
    language: "Verilog",
    imageUrl: "",
    siliconPoints: 50
  });

  // Code Question Management States
  const [codeQuestions, setCodeQuestions] = useState([]);
  const [filteredCodeQuestions, setFilteredCodeQuestions] = useState([]);
  const [codeQuestionSearch, setCodeQuestionSearch] = useState("");
  const [codeQuestionLanguageFilter, setCodeQuestionLanguageFilter] = useState("all");
  const [codeQuestionDifficultyFilter, setCodeQuestionDifficultyFilter] = useState("all");
  const [codeQuestionSortOrder, setCodeQuestionSortOrder] = useState("asc");
  const [showCodeQuestionModal, setShowCodeQuestionModal] = useState(false);
  const [showCodeQuestionBulkUploadModal, setShowCodeQuestionBulkUploadModal] = useState(false);
  const [editingCodeQuestion, setEditingCodeQuestion] = useState(null);

  // Code Question ZIP Import States
  const [codeQuestionBulkUploadType, setCodeQuestionBulkUploadType] = useState("csv"); // "csv" or "zip"
  const [codeQuestionZipFile, setCodeQuestionZipFile] = useState(null);
  const [codeQuestionZipStatus, setCodeQuestionZipStatus] = useState({ message: "", type: "", details: [] });
  const [codeQuestionZipProgress, setCodeQuestionZipProgress] = useState({ phase: "", current: 0, total: 0 });
  const [codeQuestionZipImporting, setCodeQuestionZipImporting] = useState(false);

  const [codeQuestionForm, setCodeQuestionForm] = useState({
    title: "",
    description: "",
    courseName: "",
    category: "Verilog",
    difficulty: "Medium",
    totalMarks: 100,
    passMarks: 40,
    order: 1,
    questionIMG: "",
    isProctored: false,
    initialCode: "",
    hints: [""],
    solution: "",
    testCases: "",
    isPublished: false,
    siliconPoints: 50
  });



  // Results
  const [results, setResults] = useState([]);
  const [resultsFilter, setResultsFilter] = useState("all");

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadAll = async () => {
      try {
        setLoading(true);
        await Promise.all([fetchTests(), fetchResults(), fetchCodeQuestions()]);
      } finally {
        setLoading(false);
      }
    };
    loadAll();
  }, []);

  useEffect(() => {
    filterTests();
  }, [tests, testSearch, testStatusFilter, testCategoryFilter, testTypeFilter]);

  useEffect(() => {
    filterCodeQuestions();
  }, [codeQuestions, codeQuestionSearch, codeQuestionLanguageFilter, codeQuestionDifficultyFilter, codeQuestionSortOrder]);

  const fetchTests = async () => {
    const snapshot = await getDocs(
      query(collection(db, "tests"), orderBy("scheduledAt", "desc"))
    );
    const data = snapshot.docs.map((d) => {
      const raw = d.data();
      return {
        id: d.id,
        ...raw,
        scheduledAt: raw.scheduledAt ? raw.scheduledAt.toDate() : null,
      };
    });
    setTests(data);
  };

  const filterTests = () => {
    let list = [...tests];

    if (testSearch) {
      const term = testSearch.toLowerCase();
      list = list.filter(
        (t) =>
          t.title?.toLowerCase().includes(term) ||
          t.courseName?.toLowerCase().includes(term)
      );
    }

    if (testStatusFilter !== "all") {
      const now = new Date();
      if (testStatusFilter === "upcoming") {
        list = list.filter((t) => t.scheduledAt && t.scheduledAt >= now);
      } else if (testStatusFilter === "past") {
        list = list.filter((t) => t.scheduledAt && t.scheduledAt < now);
      } else if (testStatusFilter === "published") {
        list = list.filter((t) => t.isPublished);
      } else if (testStatusFilter === "draft") {
        list = list.filter((t) => !t.isPublished);
      }
    }

    if (testCategoryFilter !== "all") {
      list = list.filter((t) => {
        const testCategory = t.category || t.courseName || t.targetGroup || '';
        return testCategory === testCategoryFilter;
      });
    }

    if (testTypeFilter !== "all") {
      list = list.filter(t => t.type === testTypeFilter);
    }

    setFilteredTests(list);
  };

  const handleTestInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setTestForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };



  const resetTestForm = () => {
    setTestForm({
      title: "",
      description: "",
      courseName: "",
      category: "",
      type: "quiz",
      date: "",
      time: "",
      duration: "",
      totalMarks: "",
      passMarks: "",
      attemptsAllowed: 1,
      targetGroup: "",
      isProctored: false,
      isPublished: false,
      initialCode: "",
      language: "Verilog",
      imageUrl: "",
      siliconPoints: 50
    });
  };

  const handleTestSubmit = async (e) => {
    e.preventDefault();
    const scheduledAt =
      testForm.date && testForm.time
        ? new Date(`${testForm.date}T${testForm.time}`)
        : null;

    const payload = {
      ...testForm,
      siliconPoints: Number(testForm.siliconPoints) || 50,
      attemptsAllowed: Number(testForm.attemptsAllowed || 1),
      totalMarks: Number(testForm.totalMarks || 0),
      passMarks: Number(testForm.passMarks || 0),
      scheduledAt,
      createdAt: editingTest?.createdAt || new Date(),
      updatedAt: new Date(),
    };

    if (editingTest) {
      await updateDoc(doc(db, "tests", editingTest.id), payload);
      setTests((prev) =>
        prev.map((t) => (t.id === editingTest.id ? { ...t, ...payload } : t))
      );
    } else {
      const ref = await addDoc(collection(db, "tests"), payload);
      setTests((prev) => [{ id: ref.id, ...payload }, ...prev]);
    }

    setShowTestModal(false);
    setEditingTest(null);
    resetTestForm();
  };

  const handleEditTest = (test) => {
    setEditingTest(test);
    setTestForm({
      title: test.title || "",
      description: test.description || "",
      courseName: test.courseName || "",
      category: test.category || "",
      type: test.type || "quiz",
      date: test.scheduledAt
        ? test.scheduledAt.toISOString().slice(0, 10)
        : "",
      time: test.scheduledAt
        ? test.scheduledAt.toTimeString().slice(0, 5)
        : "",
      duration: test.duration || "",
      totalMarks: test.totalMarks ?? "",
      passMarks: test.passMarks ?? "",
      attemptsAllowed: test.attemptsAllowed ?? 1,
      targetGroup: test.targetGroup || "",
      isProctored: Boolean(test.isProctored),
      isPublished: Boolean(test.isPublished),
      siliconPoints: test.siliconPoints !== undefined ? Number(test.siliconPoints) : 50,
    });
    setShowTestModal(true);
  };

  const handleDeleteTest = async (test) => {
    if (!window.confirm(`Delete test "${test.title}"?`)) return;
    await deleteDoc(doc(db, "tests", test.id));
    setTests((prev) => prev.filter((t) => t.id !== test.id));
  };

  const getTestStatusBadge = (test) => {
    if (!test.isPublished) return { text: "Draft", className: "bg-warning" };
    if (!test.scheduledAt) return { text: "Unscheduled", className: "bg-secondary" };
    const now = new Date();
    if (test.scheduledAt < now) return { text: "Completed", className: "bg-secondary" };
    return { text: "Upcoming", className: "bg-success" };
  };

  // ========== CODE QUESTION MANAGEMENT FUNCTIONS ==========

  const fetchCodeQuestions = async () => {
    try {
      const q = query(
        collection(db, "codingTests")
        // orderBy("createdAt", "desc") // Removed to avoid index requirement
      );
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        scheduledAt: doc.data().scheduledAt?.toDate ? doc.data().scheduledAt.toDate() : doc.data().scheduledAt,
        createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : doc.data().createdAt,
        updatedAt: doc.data().updatedAt?.toDate ? doc.data().updatedAt.toDate() : doc.data().updatedAt
      }));
      // Sort by order ascending, fallback to createdAt descending
      data.sort((a, b) => {
        const orderA = a.order !== undefined && a.order !== null && a.order !== "" ? Number(a.order) : Infinity;
        const orderB = b.order !== undefined && b.order !== null && b.order !== "" ? Number(b.order) : Infinity;
        if (orderA !== orderB) {
          return orderA - orderB;
        }
        return (b.createdAt || new Date(0)) - (a.createdAt || new Date(0));
      });
      setCodeQuestions(data);
    } catch (error) {
      console.error("Error fetching code questions:", error);
      setCodeQuestions([]); // Set empty array on error
    }
  };

  const filterCodeQuestions = () => {
    let list = [...codeQuestions];

    if (codeQuestionSearch) {
      const term = codeQuestionSearch.toLowerCase();
      list = list.filter(t =>
        t.title?.toLowerCase().includes(term) ||
        t.description?.toLowerCase().includes(term)
      );
    }

    if (codeQuestionLanguageFilter !== "all") {
      list = list.filter(t => t.category === codeQuestionLanguageFilter);
    }

    if (codeQuestionDifficultyFilter !== "all") {
      list = list.filter(t => t.difficulty === codeQuestionDifficultyFilter);
    }

    // Sort based on codeQuestionSortOrder
    list.sort((a, b) => {
      const orderA = a.order !== undefined && a.order !== null && a.order !== "" ? Number(a.order) : Infinity;
      const orderB = b.order !== undefined && b.order !== null && b.order !== "" ? Number(b.order) : Infinity;
      
      if (orderA === orderB) {
        return (b.createdAt || new Date(0)) - (a.createdAt || new Date(0));
      }
      
      return codeQuestionSortOrder === "asc" ? orderA - orderB : orderB - orderA;
    });

    setFilteredCodeQuestions(list);
  };

  const handleCodeQuestionInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCodeQuestionForm(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const addHint = () => {
    setCodeQuestionForm(prev => ({
      ...prev,
      hints: [...prev.hints, ""]
    }));
  };

  const updateHint = (index, value) => {
    setCodeQuestionForm(prev => {
      const newHints = [...prev.hints];
      newHints[index] = value;
      return { ...prev, hints: newHints };
    });
  };

  const removeHint = (index) => {
    if (codeQuestionForm.hints.length > 1) {
      setCodeQuestionForm(prev => ({
        ...prev,
        hints: prev.hints.filter((_, i) => i !== index)
      }));
    }
  };

  const handleCodeQuestionSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        title: codeQuestionForm.title,
        description: codeQuestionForm.description,
        courseName: codeQuestionForm.courseName,
        category: codeQuestionForm.category,
        difficulty: codeQuestionForm.difficulty,
        totalMarks: Number(codeQuestionForm.totalMarks) || 100,
        passMarks: Number(codeQuestionForm.passMarks) || 40,
        order: Number(codeQuestionForm.order) || 1,
        questionIMG: codeQuestionForm.questionIMG || "",
        isProctored: codeQuestionForm.isProctored,
        initialCode: codeQuestionForm.initialCode,
        hints: codeQuestionForm.hints.filter(h => h.trim() !== ""),
        solution: codeQuestionForm.solution,
        testCases: codeQuestionForm.testCases,
        isPublished: codeQuestionForm.isPublished,
        siliconPoints: Number(codeQuestionForm.siliconPoints) || 50,
        createdAt: editingCodeQuestion?.createdAt || new Date(),
        updatedAt: new Date()
      };

      if (editingCodeQuestion) {
        await updateDoc(doc(db, "codingTests", editingCodeQuestion.id), payload);
        setCodeQuestions(prev =>
          prev.map(t => t.id === editingCodeQuestion.id ? { ...t, ...payload } : t)
        );
      } else {
        const ref = await addDoc(collection(db, "codingTests"), payload);
        setCodeQuestions(prev => [{ id: ref.id, ...payload }, ...prev]);
      }

      setShowCodeQuestionModal(false);
      setEditingCodeQuestion(null);
      resetCodeQuestionForm();
    } catch (error) {
      console.error("Error saving code question:", error);
      alert("Failed to save code question. Please try again.");
    }
  };

  // Coding Question Image Handling Functions
  const scanCodeExistingImages = (content) => {
    if (!content) return [];
    const regex = /<img[^>]+src="([^">]+)"/g;
    const urls = [];
    let match;
    while ((match = regex.exec(content)) !== null) {
      urls.push(match[1]);
    }
    return urls;
  };

  const handleCodeImageFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCodeImageFile(file);
      setCodeImageError("");
    }
  };

  const insertCodeImageAtCursor = (url) => {
    const textarea = codeQuestionTextareaRef.current;
    const imgTag = `<img src="${url}" alt="Code Problem Image" style="width: 360px; height: 300px; border-radius: 8px; margin: 1rem 0; display: block; object-fit: contain;" />`;

    if (!textarea) {
      setCodeQuestionForm(prev => ({
        ...prev,
        description: (prev.description || "") + `\n${imgTag}`
      }));
      return;
    }

    const startPos = textarea.selectionStart;
    const endPos = textarea.selectionEnd;
    const text = codeQuestionForm.description || "";
    const newText = text.substring(0, startPos) + imgTag + text.substring(endPos);

    setCodeQuestionForm(prev => ({
      ...prev,
      description: newText
    }));

    setTimeout(() => {
      textarea.focus();
      textarea.selectionStart = textarea.selectionEnd = startPos + imgTag.length;
    }, 50);
  };

  const uploadCodeImageToFirebase = () => {
    if (!codeImageFile) {
      setCodeImageError("Please select an image file first.");
      return;
    }

    if (!auth.currentUser) {
      setCodeImageError("You must be logged in to upload images.");
      return;
    }

    setUploadingCodeImage(true);
    setCodeImageProgress(0);
    setCodeImageError("");

    try {
      console.log("Starting code image upload for file:", codeImageFile.name, "| Size:", (codeImageFile.size / 1024).toFixed(1), "KB");
      console.log("Current user UID:", auth.currentUser.uid);

      const fileSizeMB = codeImageFile.size / 1024 / 1024;
      if (fileSizeMB > 5) {
        setUploadingCodeImage(false);
        setCodeImageError("Image must be smaller than 5MB.");
        return;
      }

      const timestamp = Date.now();
      const categoryFolder = (codeQuestionForm.category || "General")
        .replace(/[^a-zA-Z0-9]/g, "_")
        .replace(/_+/g, "_")
        .toLowerCase();

      const safeFileName = codeImageFile.name.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9._-]/g, '');
      const uniqueFileName = `code_questions/${categoryFolder}/${timestamp}_${safeFileName}`;
      console.log("Upload path:", uniqueFileName);
      const storageRef = ref(storage, uniqueFileName);

      const uploadMetadata = {
        contentType: codeImageFile.type,
      };

      const task = uploadBytesResumable(storageRef, codeImageFile, uploadMetadata);

      task.on(
        "state_changed",
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log(`Upload progress: ${Math.round(progress)}% (${snapshot.bytesTransferred}/${snapshot.totalBytes} bytes)`);
          setCodeImageProgress(Math.round(progress));
        },
        (error) => {
          console.error("Firebase Storage upload error:", error.code, error.message);
          let userMsg = `Upload failed: ${error.message}`;
          if (error.code === 'storage/unauthorized') {
            userMsg = "Permission denied. Make sure storage rules allow admin uploads to code_questions/. Deploy the updated storage.rules.";
          } else if (error.code === 'storage/canceled') {
            userMsg = "Upload was cancelled.";
          } else if (error.code === 'storage/unknown') {
            userMsg = "Unknown error. Check browser console and Firebase Storage rules.";
          } else if (error.code === 'storage/quota-exceeded') {
            userMsg = "Firebase Storage quota exceeded.";
          } else if (error.code === 'storage/unauthenticated') {
            userMsg = "Session expired. Please log out and log back in.";
          }
          setUploadingCodeImage(false);
          setCodeImageError(userMsg);
        },
        async () => {
          try {
            console.log("Upload completed, fetching download URL...");
            const downloadUrl = await getDownloadURL(storageRef);
            console.log("Successfully fetched download URL:", downloadUrl);
            setCodeImageProgress(100);

            setTimeout(() => {
              setUploadingCodeImage(false);
              setCodeImageSuccess(true);
              setCodeImageFile(null);
              setUploadedCodeImages((prev) => [...prev, downloadUrl]);
              setTimeout(() => setCodeImageSuccess(false), 4000);
            }, 600);

            insertCodeImageAtCursor(downloadUrl);
          } catch (urlError) {
            setUploadingCodeImage(false);
            setCodeImageError(`Failed to fetch image URL: ${urlError.message}`);
          }
        }
      );
    } catch (err) {
      setUploadingCodeImage(false);
      setCodeImageError(`Upload setup failed: ${err.message}`);
    }
  };

  const handleEditCodeQuestion = (test) => {
    setEditingCodeQuestion(test);
    setCodeQuestionForm({
      title: test.title || "",
      description: test.description || "",
      courseName: test.courseName || "",
      category: test.category || "Verilog",
      difficulty: test.difficulty || "Medium",
      totalMarks: test.totalMarks !== undefined ? test.totalMarks : 100,
      passMarks: test.passMarks !== undefined ? test.passMarks : 40,
      order: test.order !== undefined ? test.order : 1,
      isProctored: Boolean(test.isProctored),
      initialCode: test.initialCode || "",
      hints: test.hints?.length > 0 ? test.hints : [""],
      solution: test.solution || "",
      testCases: test.testCases || "",
      isPublished: Boolean(test.isPublished),
      siliconPoints: test.siliconPoints !== undefined ? Number(test.siliconPoints) : 50
    });

    // Scan for existing images in description
    const existing = scanCodeExistingImages(test.description);
    setUploadedCodeImages(existing);
    setCodeImageFile(null);
    setCodeImageProgress(0);
    setCodeImageError("");
    setCodeImageSuccess(false);

    setShowCodeQuestionModal(true);
  };

  const handleDeleteCodeQuestion = async (test) => {
    if (!window.confirm(`Delete "${test.title}"? This action cannot be undone.`)) return;

    try {
      await deleteDoc(doc(db, "codingTests", test.id));
      setCodeQuestions(prev => prev.filter(t => t.id !== test.id));
    } catch (error) {
      console.error("Error deleting code question:", error);
      alert("Failed to delete code question. Please try again.");
    }
  };

  const resetCodeQuestionForm = () => {
    setCodeQuestionForm({
      title: "",
      description: "",
      courseName: "",
      category: "Verilog",
      difficulty: "Medium",
      totalMarks: 100,
      passMarks: 40,
      order: 1,
      isProctored: false,
      initialCode: "",
      hints: [""],
      solution: "",
      testCases: "",
      isPublished: false,
      siliconPoints: 50
    });
    setEditingCodeQuestion(null);
    setUploadedCodeImages([]);
    setCodeImageFile(null);
    setCodeImageProgress(0);
    setCodeImageError("");
    setCodeImageSuccess(false);
  };

  // View Test & Manage Questions
  const handleViewTest = async (test) => {
    setViewingTest(test);
    setShowViewTestModal(true);
    // Fetch questions for this test
    const questionsSnapshot = await getDocs(
      query(collection(db, "testQuestions"), where("testId", "==", test.id))
    );
    const questionsData = questionsSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    setTestQuestions(questionsData);
  };

  const handleTestQuestionInputChange = (e) => {
    const { name, value } = e.target;
    setTestQuestionForm((prev) => ({ ...prev, [name]: value }));
  };

  // Question Image Handling Functions
  const scanQuestionExistingImages = (content) => {
    if (!content) return [];
    const regex = /<img[^>]+src="([^">]+)"/g;
    const urls = [];
    let match;
    while ((match = regex.exec(content)) !== null) {
      urls.push(match[1]);
    }
    return urls;
  };

  const handleQuestionImageFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setQuestionImageFile(file);
      setQuestionImageError("");
    }
  };

  const insertQuestionImageAtCursor = (url) => {
    const textarea = testQuestionTextareaRef.current;
    const imgTag = `<img src="${url}" alt="Question Image" style="width: 360px; height: 300px; border-radius: 8px; margin: 1rem 0; display: block; object-fit: contain;" />`;

    if (!textarea) {
      setTestQuestionForm(prev => ({
        ...prev,
        questionText: (prev.questionText || "") + `\n${imgTag}`
      }));
      return;
    }

    const startPos = textarea.selectionStart;
    const endPos = textarea.selectionEnd;
    const text = testQuestionForm.questionText || "";
    const newText = text.substring(0, startPos) + imgTag + text.substring(endPos);

    setTestQuestionForm(prev => ({
      ...prev,
      questionText: newText
    }));

    setTimeout(() => {
      textarea.focus();
      textarea.selectionStart = textarea.selectionEnd = startPos + imgTag.length;
    }, 50);
  };

  const uploadQuestionImageToFirebase = () => {
    if (!questionImageFile) {
      setQuestionImageError("Please select an image file first.");
      return;
    }

    if (!auth.currentUser) {
      setQuestionImageError("You must be logged in to upload images.");
      return;
    }

    setUploadingQuestionImage(true);
    setQuestionImageProgress(0);
    setQuestionImageError("");

    try {
      console.log("Starting question image upload for file:", questionImageFile.name, "| Size:", (questionImageFile.size / 1024).toFixed(1), "KB");
      console.log("Current user UID:", auth.currentUser.uid);

      const fileSizeMB = questionImageFile.size / 1024 / 1024;
      if (fileSizeMB > 5) {
        setUploadingQuestionImage(false);
        setQuestionImageError("Image must be smaller than 5MB.");
        return;
      }

      const timestamp = Date.now();
      const categoryFolder = (viewingTest?.category || "General")
        .replace(/[^a-zA-Z0-9]/g, "_")
        .replace(/_+/g, "_")
        .toLowerCase();

      const safeFileName = questionImageFile.name.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9._-]/g, '');
      const uniqueFileName = `test_questions/${categoryFolder}/${timestamp}_${safeFileName}`;
      console.log("Upload path:", uniqueFileName);
      const storageRef = ref(storage, uniqueFileName);

      const uploadMetadata = {
        contentType: questionImageFile.type,
      };

      const task = uploadBytesResumable(storageRef, questionImageFile, uploadMetadata);

      task.on(
        "state_changed",
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log(`Upload progress: ${Math.round(progress)}% (${snapshot.bytesTransferred}/${snapshot.totalBytes} bytes)`);
          setQuestionImageProgress(Math.round(progress));
        },
        (error) => {
          console.error("Firebase Storage upload error:", error.code, error.message);
          let userMsg = `Upload failed: ${error.message}`;
          if (error.code === 'storage/unauthorized') {
            userMsg = "Permission denied. Make sure storage rules allow admin uploads to test_questions/. Deploy the updated storage.rules.";
          } else if (error.code === 'storage/canceled') {
            userMsg = "Upload was cancelled.";
          } else if (error.code === 'storage/unknown') {
            userMsg = "Unknown error. Check browser console and Firebase Storage rules.";
          } else if (error.code === 'storage/quota-exceeded') {
            userMsg = "Firebase Storage quota exceeded.";
          } else if (error.code === 'storage/unauthenticated') {
            userMsg = "Session expired. Please log out and log back in.";
          }
          setUploadingQuestionImage(false);
          setQuestionImageError(userMsg);
        },
        async () => {
          try {
            console.log("Upload completed, fetching download URL...");
            const downloadUrl = await getDownloadURL(storageRef);
            console.log("Successfully fetched download URL:", downloadUrl);
            setQuestionImageProgress(100);

            setTimeout(() => {
              setUploadingQuestionImage(false);
              setQuestionImageSuccess(true);
              setQuestionImageFile(null);
              setUploadedQuestionImages((prev) => [...prev, downloadUrl]);
              setTimeout(() => setQuestionImageSuccess(false), 4000);
            }, 600);

            insertQuestionImageAtCursor(downloadUrl);
          } catch (urlError) {
            setUploadingQuestionImage(false);
            setQuestionImageError(`Failed to fetch image URL: ${urlError.message}`);
          }
        }
      );
    } catch (err) {
      setUploadingQuestionImage(false);
      setQuestionImageError(`Upload setup failed: ${err.message}`);
    }
  };

  const resetTestQuestionForm = () => {
    setTestQuestionForm({
      questionText: "",
      optionA: "",
      optionB: "",
      optionC: "",
      optionD: "",
      correctOption: "A",
      marks: 1,
      explanation: "",
      imageUrl: "",
    });
    setEditingQuestion(null);
    setUploadedQuestionImages([]);
    setQuestionImageFile(null);
    setQuestionImageProgress(0);
    setQuestionImageError("");
    setQuestionImageSuccess(false);
  };

  const handleAddQuestion = () => {
    resetTestQuestionForm();
    setShowQuestionForm(true);
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion(question);
    setTestQuestionForm({
      questionText: question.questionText || "",
      optionA: question.options?.A || "",
      optionB: question.options?.B || "",
      optionC: question.options?.C || "",
      optionD: question.options?.D || "",
      correctOption: question.correctOption || "A",
      marks: question.marks || 1,
      explanation: question.explanation || "",
      imageUrl: question.imageUrl || "",
    });

    // Scan for existing images
    const existing = scanQuestionExistingImages(question.questionText);
    setUploadedQuestionImages(existing);
    setQuestionImageFile(null);
    setQuestionImageProgress(0);
    setQuestionImageError("");
    setQuestionImageSuccess(false);

    setShowQuestionForm(true);
  };

  const handleTestQuestionSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      testId: viewingTest.id,
      questionText: testQuestionForm.questionText,
      options: {
        A: testQuestionForm.optionA,
        B: testQuestionForm.optionB,
        C: testQuestionForm.optionC,
        D: testQuestionForm.optionD,
      },
      correctOption: testQuestionForm.correctOption,
      marks: Number(testQuestionForm.marks) || 1,
      explanation: testQuestionForm.explanation,
      imageUrl: testQuestionForm.imageUrl || "",
      createdAt: editingQuestion?.createdAt || new Date(),
      updatedAt: new Date(),
    };

    if (editingQuestion) {
      await updateDoc(doc(db, "testQuestions", editingQuestion.id), payload);
      setTestQuestions((prev) =>
        prev.map((q) => (q.id === editingQuestion.id ? { ...q, ...payload } : q))
      );
    } else {
      const docRef = await addDoc(collection(db, "testQuestions"), payload);
      setTestQuestions((prev) => [...prev, { id: docRef.id, ...payload }]);
    }

    resetTestQuestionForm();
    setShowQuestionForm(false);
  };

  const handleDeleteQuestion = async (question) => {
    if (!window.confirm(`Delete this question?`)) return;
    await deleteDoc(doc(db, "testQuestions", question.id));
    setTestQuestions((prev) => prev.filter((q) => q.id !== question.id));
  };

  const calculateTotalMarks = () => {
    return testQuestions.reduce((sum, q) => sum + (Number(q.marks) || 0), 0);
  };

  // ==================== QUESTIONS ZIP BUNDLE IMPORT FUNCTIONS ====================

  const handleQuestionZipFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setQuestionZipFile(file);
      setQuestionZipStatus({ message: "", type: "", details: [] });
    }
  };

  const getDummyPngBase64 = () => {
    return "iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==";
  };

  const downloadQuestionZipTemplate = async () => {
    try {
      const zip = new JSZip();

      // Sample CSV with image placeholders
      const csvContent =
        `Question Text,Option A,Option B,Option C,Option D,Correct Option (A/B/C/D),Marks,Explanation (Optional)
"What logic gate is represented by this circuit diagram?{{img:gate_circuit.png}}","AND Gate","OR Gate","NAND Gate","NOR Gate","C",1,"The schematic shows a NAND gate circuit implemented with transistors."
"Calculate the output voltage Vout for the amplifier circuit below:{{img:amplifier.png}}","2V","5V","10V","12V","B",2,"Vout = - (Rf / Rin) * Vin = - (50k / 10k) * (-1V) = 5V."
"What is the binary equivalent of decimal number 10?","1001","1010","1100","1111","B",1,"10 in binary is 1010."`;

      zip.file("questions.csv", csvContent);

      // Add placeholder images (1x1 PNG)
      const pngData = getDummyPngBase64();
      const pngBytes = Uint8Array.from(atob(pngData), c => c.charCodeAt(0));
      zip.folder("images").file("gate_circuit.png", pngBytes);
      zip.folder("images").file("amplifier.png", pngBytes);
      zip.file("README.txt",
        `HOW TO USE THIS TEMPLATE
========================

1. Edit questions.csv with your questions, options, correct answer option, and marks.
2. Use {{img:filename.ext}} placeholders in the "Question Text" column where you want images.
3. Put the real images in the images/ folder.
   - Image filenames must match exactly what you wrote in {{img:...}} placeholders.
4. Keep this ZIP structure:
   questions_import.zip/
   ├── questions.csv
   └── images/
       ├── gate_circuit.png
       ├── amplifier.png
       └── ...
5. Upload this ZIP using the "Import Bundle (ZIP)" panel in the View Test Questions section.
`);

      const content = await zip.generateAsync({ type: "blob" });
      const url = window.URL.createObjectURL(content);
      const a = document.createElement("a");
      a.href = url;
      a.download = "questions_import_template.zip";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error generating questions ZIP template:", err);
    }
  };

  const uploadQuestionZipImages = async (imageFilesMap, categoryFolder) => {
    const imageUrlMap = {};
    const filenames = Object.keys(imageFilesMap);
    let uploadedCount = 0;

    for (const filename of filenames) {
      try {
        setQuestionZipProgress(prev => ({
          ...prev,
          phase: "Uploading Images",
          current: uploadedCount + 1,
          total: filenames.length
        }));

        const fileData = await imageFilesMap[filename].async("arraybuffer");
        const ext = filename.split(".").pop().toLowerCase();
        const mimeTypes = {
          png: "image/png",
          jpg: "image/jpeg",
          jpeg: "image/jpeg",
          gif: "image/gif",
          webp: "image/webp",
          svg: "image/svg+xml",
        };
        const contentType = mimeTypes[ext] || "image/png";

        const timestamp = Date.now();
        const safeFilename = filename.replace(/\s+/g, "_").replace(/[^a-zA-Z0-9._-]/g, "");
        const storagePath = `test_questions/${categoryFolder}/${timestamp}_${safeFilename}`;
        const storageRef = ref(storage, storagePath);

        const blob = new Blob([fileData], { type: contentType });
        const uploadTask = uploadBytesResumable(storageRef, blob, { contentType });

        await new Promise((resolve, reject) => {
          uploadTask.on(
            "state_changed",
            null,
            (error) => {
              console.error(`Firebase Storage upload error for ${filename}:`, error.code, error.message);
              let userMsg = error.message;
              if (error.code === 'storage/unauthorized') {
                userMsg = `Permission denied uploading image "${filename}". Make sure storage rules allow admin uploads to test_questions/. Deploy the updated storage.rules.`;
              }
              reject(new Error(userMsg));
            },
            async () => {
              const downloadUrl = await getDownloadURL(storageRef);
              imageUrlMap[filename] = downloadUrl;
              resolve();
            }
          );
        });

        uploadedCount++;
      } catch (err) {
        console.error(`Failed to upload image "${filename}":`, err);
        imageUrlMap[filename] = null;
      }
    }

    return imageUrlMap;
  };

  const resolveQuestionImagePlaceholders = (content, imageUrlMap) => {
    return content.replace(/\{\{img:([^}]+)\}\}/g, (match, filename) => {
      const url = imageUrlMap[filename.trim()];
      if (url) {
        return `<img src="${url}" alt="${filename.trim()}" style="width: 360px; height: 300px; border-radius: 8px; margin: 1rem 0; display: block; object-fit: contain;" />`;
      }
      return `<span style="color:red;font-weight:bold;">[IMAGE NOT FOUND: ${filename.trim()}]</span>`;
    });
  };

  const processQuestionZipBundle = async () => {
    if (!questionZipFile || !viewingTest) {
      setQuestionZipStatus({
        message: "Please select a ZIP bundle file.",
        type: "error",
        details: []
      });
      return;
    }

    if (!auth.currentUser) {
      setQuestionZipStatus({
        message: "You must be logged in to import questions with images.",
        type: "error",
        details: []
      });
      return;
    }

    setQuestionZipImporting(true);
    setQuestionZipStatus({ message: "", type: "", details: [] });

    try {
      setQuestionZipProgress({ phase: "Extracting ZIP", current: 0, total: 0 });
      const zip = await JSZip.loadAsync(questionZipFile);

      // Find questions.csv
      let csvFile = null;
      zip.forEach((relativePath, file) => {
        if (!file.dir && relativePath.toLowerCase().endsWith("questions.csv")) {
          csvFile = file;
        }
      });

      if (!csvFile) {
        setQuestionZipImporting(false);
        setQuestionZipStatus({
          message: "No questions.csv file found in the ZIP bundle.",
          type: "error",
          details: ["Make sure your ZIP contains a file named 'questions.csv' in the root or a subfolder."]
        });
        return;
      }

      // Extract CSV text
      const csvText = await csvFile.async("text");
      const parsedRows = parseCSV(csvText);

      if (parsedRows.length < 2) {
        setQuestionZipImporting(false);
        setQuestionZipStatus({
          message: "CSV file is empty or invalid.",
          type: "error",
          details: ["Make sure the CSV has a header row and at least one question row."]
        });
        return;
      }

      // Find all image files
      const imageFilesMap = {};
      zip.forEach((relativePath, file) => {
        if (!file.dir) {
          const lowerPath = relativePath.toLowerCase();
          const isImage = /\.(png|jpg|jpeg|gif|webp|svg)$/.test(lowerPath);
          if (isImage) {
            const filename = relativePath.split("/").pop();
            imageFilesMap[filename] = file;
          }
        }
      });

      const imageCount = Object.keys(imageFilesMap).length;

      // Upload images
      let imageUrlMap = {};
      if (imageCount > 0) {
        const categoryFolder = (viewingTest?.category || "General")
          .replace(/[^a-zA-Z0-9]/g, "_")
          .replace(/_+/g, "_")
          .toLowerCase();

        imageUrlMap = await uploadQuestionZipImages(imageFilesMap, categoryFolder);
      }

      // Save questions
      const dataRows = parsedRows.slice(1);
      setQuestionZipProgress({ phase: "Saving Questions", current: 0, total: dataRows.length });
      let successCount = 0;
      const errors = [];

      for (let i = 0; i < dataRows.length; i++) {
        const row = dataRows[i];

        if (row.length < 6) {
          errors.push(`Row ${i + 2}: Insufficient columns`);
          continue;
        }

        const [questionText, optionA, optionB, optionC, optionD, correctOption, marks, explanation] = row;

        if (!questionText || !optionA || !optionB) {
          errors.push(`Row ${i + 2}: Missing required fields (Question Text, Option A, Option B)`);
          continue;
        }

        const correctOpt = correctOption.toUpperCase().trim();
        if (!['A', 'B', 'C', 'D'].includes(correctOpt)) {
          errors.push(`Row ${i + 2}: Invalid correct option (must be A, B, C, or D)`);
          continue;
        }

        try {
          const resolvedText = resolveQuestionImagePlaceholders(questionText.trim(), imageUrlMap);

          const payload = {
            testId: viewingTest.id,
            questionText: resolvedText,
            options: {
              A: optionA.trim(),
              B: optionB.trim(),
              C: optionC?.trim() || "",
              D: optionD?.trim() || "",
            },
            correctOption: correctOpt,
            marks: Number(marks) || 1,
            explanation: explanation?.trim() || "",
            imageUrl: "", // We embed the image directly inline inside questionText instead of using imageUrl!
            createdAt: new Date(),
            updatedAt: new Date(),
          };

          const docRef = await addDoc(collection(db, "testQuestions"), payload);
          setTestQuestions((prev) => [...prev, { id: docRef.id, ...payload }]);
          successCount++;
        } catch (err) {
          errors.push(`Row ${i + 2}: ${err.message}`);
        }
      }

      // Finish
      setQuestionZipImporting(false);
      setQuestionZipProgress({ phase: "Done", current: successCount, total: dataRows.length });

      if (errors.length > 0) {
        setQuestionZipStatus({
          message: `Imported ${successCount} of ${dataRows.length} questions with errors.`,
          type: "warning",
          details: errors
        });
      } else {
        setQuestionZipStatus({
          message: `Successfully imported all ${successCount} questions with ${imageCount} images! 🎉`,
          type: "success",
          details: []
        });
      }

      // Clean up after some time
      setTimeout(() => {
        setShowQuestionZipImport(false);
        setQuestionZipFile(null);
        setQuestionZipStatus({ message: "", type: "", details: [] });
      }, 5000);

    } catch (err) {
      console.error("ZIP questions import error:", err);
      setQuestionZipImporting(false);
      setQuestionZipStatus({
        message: `Import failed: ${err.message}`,
        type: "error",
        details: []
      });
    }
  };

  // CSV Upload Functions
  const downloadCSVTemplate = () => {
    const headers = [
      "Question Text",
      "Option A",
      "Option B",
      "Option C",
      "Option D",
      "Correct Option (A/B/C/D)",
      "Marks",
      "Explanation (Optional)",
      "Image URL (Optional)"
    ];

    const sampleRow = [
      "What is the capital of France?",
      "London",
      "Paris",
      "Berlin",
      "Madrid",
      "B",
      "1",
      "Paris is the capital and most populous city of France.",
      "https://example.com/image.jpg"
    ];

    const csvContent = [
      headers.join(","),
      sampleRow.map(cell => `\"${cell}\"`).join(",")
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "question_template.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const parseCSV = (text) => {
    const lines = [];
    let currentLine = [];
    let currentField = "";
    let inQuotes = false;

    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const nextChar = text[i + 1];

      if (char === '"' && nextChar === '"' && inQuotes) {
        currentField += '"';
        i++;
      } else if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        currentLine.push(currentField.trim());
        currentField = "";
      } else if ((char === '\n' || char === '\r') && !inQuotes) {
        if (currentField || currentLine.length > 0) {
          currentLine.push(currentField.trim());
          if (currentLine.some(field => field)) {
            lines.push(currentLine);
          }
          currentLine = [];
          currentField = "";
        }
        if (char === '\r' && nextChar === '\n') i++;
      } else {
        currentField += char;
      }
    }

    if (currentField || currentLine.length > 0) {
      currentLine.push(currentField.trim());
      if (currentLine.some(field => field)) {
        lines.push(currentLine);
      }
    }

    return lines;
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      setCsvUploadStatus({ message: "Please upload a CSV file", type: "error" });
      return;
    }

    setCsvFile(file);
    setCsvUploadStatus({ message: "", type: "" });
  };

  const processCSVFile = async () => {
    if (!csvFile) {
      setCsvUploadStatus({ message: "Please select a CSV file first", type: "error" });
      return;
    }

    try {
      const text = await csvFile.text();
      const rows = parseCSV(text);

      if (rows.length < 2) {
        setCsvUploadStatus({ message: "CSV file is empty or invalid", type: "error" });
        return;
      }

      // Skip header row
      const dataRows = rows.slice(1);
      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      for (let i = 0; i < dataRows.length; i++) {
        const row = dataRows[i];

        if (row.length < 6) {
          errors.push(`Row ${i + 2}: Insufficient columns`);
          errorCount++;
          continue;
        }

        const [questionText, optionA, optionB, optionC, optionD, correctOption, marks, explanation, imageUrl] = row;

        if (!questionText || !optionA || !optionB) {
          errors.push(`Row ${i + 2}: Missing required fields`);
          errorCount++;
          continue;
        }

        const correctOpt = correctOption.toUpperCase().trim();
        if (!['A', 'B', 'C', 'D'].includes(correctOpt)) {
          errors.push(`Row ${i + 2}: Invalid correct option (must be A, B, C, or D)`);
          errorCount++;
          continue;
        }

        try {
          const payload = {
            testId: viewingTest.id,
            questionText: questionText.trim(),
            options: {
              A: optionA.trim(),
              B: optionB.trim(),
              C: optionC?.trim() || "",
              D: optionD?.trim() || "",
            },
            correctOption: correctOpt,
            marks: Number(marks) || 1,
            explanation: explanation?.trim() || "",
            imageUrl: imageUrl?.trim() || "",
            createdAt: new Date(),
            updatedAt: new Date(),
          };

          const docRef = await addDoc(collection(db, "testQuestions"), payload);
          setTestQuestions((prev) => [...prev, { id: docRef.id, ...payload }]);
          successCount++;
        } catch (err) {
          errors.push(`Row ${i + 2}: ${err.message}`);
          errorCount++;
        }
      }

      if (successCount > 0) {
        setCsvUploadStatus({
          message: `Successfully imported ${successCount} question(s). ${errorCount > 0 ? `Failed: ${errorCount}` : ""}`,
          type: "success",
          errors: errors.length > 0 ? errors : null
        });
      } else {
        setCsvUploadStatus({
          message: "Failed to import questions",
          type: "error",
          errors: errors
        });
      }

      // Reset after a delay if successful
      if (successCount > 0) {
        setTimeout(() => {
          setShowCSVUpload(false);
          setCsvFile(null);
          setCsvUploadStatus({ message: "", type: "" });
        }, 3000);
      }
    } catch (error) {
      setCsvUploadStatus({ message: `Error processing file: ${error.message}`, type: "error" });
    }
  };

  // CSV Functions for Bulk Quizz Test Import
  const downloadTestCSVTemplate = () => {
    const headers = [
      "Title",
      "Description",
      "Module Name",
      "Category",
      "Test Type",
      "Date (YYYY-MM-DD)",
      "Time (HH:MM)",
      "Duration",
      "Total Marks",
      "Pass Marks",
      "Attempts Allowed",
      "Target Group",
      "Is Proctored (true/false)",
      "Is Published (true/false)"
    ];

    const sampleData = [
      [
        "VLSI Design Fundamentals Quiz",
        "A comprehensive quiz covering basic VLSI design concepts",
        "VLSI Design Course",
        "VLSI Design",
        "quiz",
        "2025-02-15",
        "10:00",
        "60 minutes",
        "100",
        "40",
        "2",
        "VLSI Batch 2024",
        "false",
        "true"
      ]
    ];

    let csvContent = headers.join(",") + "\n";
    sampleData.forEach(row => {
      csvContent += row.map(cell => `"${cell}"`).join(",") + "\n";
    });

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "quiz_template.csv";
    link.click();
  };

  const parseTestCSV = (text) => {
    const lines = text.trim().split("\n");
    const headers = lines[0].split(",").map(h => h.trim().replace(/^"|"$/g, ""));
    const rows = [];

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      let inQuotes = false;
      let currentField = "";
      const fields = [];

      for (let j = 0; j < line.length; j++) {
        const char = line[j];
        const nextChar = line[j + 1];

        if (char === '"') {
          if (inQuotes && nextChar === '"') {
            currentField += '"';
            j++;
          } else {
            inQuotes = !inQuotes;
          }
        } else if (char === "," && !inQuotes) {
          fields.push(currentField.trim());
          currentField = "";
        } else {
          currentField += char;
        }
      }

      fields.push(currentField.trim());

      if (fields.length === headers.length && fields.some(f => f)) {
        const obj = {};
        headers.forEach((header, index) => {
          obj[header] = fields[index];
        });
        rows.push(obj);
      }
    }

    return rows;
  };

  const processBulkTestCSV = async () => {
    if (!csvFile) {
      setCsvUploadStatus({ message: "Please select a CSV file to import.", type: "error" });
      return;
    }

    try {
      setCsvUploadStatus({ message: "Importing tests...", type: "info" });
      const text = await csvFile.text();
      const rows = parseTestCSV(text);

      if (rows.length === 0) {
        setCsvUploadStatus({ message: "CSV file is empty or invalid", type: "error" });
        return;
      }

      let added = 0;
      let errors = [];

      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];

        try {
          // Parse date and time
          const dateStr = row["Date (YYYY-MM-DD)"];
          const timeStr = row["Time (HH:MM)"];
          let scheduledAt = null;

          if (dateStr && timeStr) {
            scheduledAt = new Date(`${dateStr}T${timeStr}`);
            if (isNaN(scheduledAt.getTime())) {
              scheduledAt = null;
            }
          }

          const testData = {
            title: row.Title || "",
            description: row.Description || "",
            courseName: row["Module Name"] || "",
            category: row.Category || "",
            type: row["Test Type"] || "quiz",
            duration: row.Duration || "",
            totalMarks: Number(row["Total Marks"]) || 0,
            passMarks: Number(row["Pass Marks"]) || 0,
            attemptsAllowed: Number(row["Attempts Allowed"]) || 1,
            targetGroup: row["Target Group"] || "",
            isProctored: row["Is Proctored (true/false)"]?.toLowerCase() === "true",
            isPublished: row["Is Published (true/false)"]?.toLowerCase() === "true",
            initialCode: row["Initial Code (Optional - for Code Tests)"] || "",
            imageUrl: row["Image URL (Optional - for Code Tests)"] || "",
            // If type is codeTest, store category as language indirectly via category field itself 
            language: (row["Test Type"] === "codeTest") ? (row.Category || "Verilog") : "Verilog",
            scheduledAt: scheduledAt,
            createdAt: new Date(),
            updatedAt: new Date()
          };

          if (!testData.title.trim()) {
            errors.push(`Row ${i + 2}: Title is required`);
            continue;
          }

          await addDoc(collection(db, "tests"), testData);
          added++;
        } catch (err) {
          errors.push(`Row ${i + 2}: ${err.message}`);
        }
      }

      if (added > 0) {
        setCsvUploadStatus({
          message: `Successfully imported ${added} test(s). ${errors.length > 0 ? `Failed: ${errors.length}` : ""}`,
          type: "success",
          errors: errors.length > 0 ? errors : null
        });
        fetchTests();
        setTimeout(() => {
          setShowBulkUploadModal(false);
          setCsvFile(null);
          setCsvUploadStatus({ message: "", type: "" });
        }, 3000);
      } else {
        setCsvUploadStatus({
          message: "Failed to import tests",
          type: "error",
          errors: errors
        });
      }
    } catch (error) {
      console.error("Error importing bulk tests:", error);
      setCsvUploadStatus({ message: "Error importing tests", type: "error" });
    }
  };

  // CSV Functions for Bulk Code Question Import
  const downloadCodeQuestionCSVTemplate = () => {
    const headers = [
      "Title",
      "Description",
      "Module Name",
      "Category",
      "Difficulty",
      "Total Marks",
      "Pass Marks",
      "Order",
      "Question Image URL",
      "Is Proctored (true/false)",
      "Is Published (true/false)",
      "Initial Code",
      "Hints (separated by |)",
      "Solution",
      "Test Cases"
    ];

    const sampleRow = [
      "4-bit Adder Design",
      "Implement a 4-bit adder module in Verilog",
      "Digital Design",
      "Verilog",
      "Medium",
      "100",
      "40",
      "1",
      "https://example.com/image.png",
      "false",
      "true",
      "module adder(input [3:0] a, b, output [3:0] sum); endmodule",
      "Use assign statement | Check overflow",
      "module adder(input [3:0] a, b, output [3:0] sum); assign sum = a + b; endmodule",
      "Test case description..."
    ];

    const csvContent = [
      headers.join(","),
      sampleRow.map(cell => `\"${cell}\"`).join(",")
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "code_question_template.csv");
    link.click();
  };

  const processBulkCodeQuestionCSV = async () => {
    if (!csvFile) {
      setCsvUploadStatus({ message: "Please select a CSV file to import.", type: "error" });
      return;
    }

    try {
      setCsvUploadStatus({ message: "Importing code questions...", type: "info" });
      const text = await csvFile.text();
      const rows = parseTestCSV(text);

      if (rows.length === 0) {
        setCsvUploadStatus({ message: "CSV file is empty or invalid", type: "error" });
        return;
      }

      let added = 0;
      let errors = [];

      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];

        try {
          const testData = {
            title: row.Title || "",
            description: row.Description || "",
            courseName: row["Module Name"] || "",
            category: row["Category"] || "Verilog",
            difficulty: row["Difficulty"] || "Medium",
            totalMarks: Number(row["Total Marks"]) || 100,
            passMarks: Number(row["Pass Marks"]) || 40,
            order: Number(row["Order"]) || 1,
            questionIMG: row["Question Image URL"] || "",
            isProctored: row["Is Proctored (true/false)"]?.toLowerCase() === "true",
            isPublished: row["Is Published (true/false)"]?.toLowerCase() === "true",
            initialCode: row["Initial Code"] || "",
            hints: row["Hints (separated by |)"]?.split("|").map(h => h.trim()).filter(h => h) || [""],
            solution: row["Solution"] || "",
            testCases: row["Test Cases"] || "",
            createdAt: new Date(),
            updatedAt: new Date()
          };

          if (!testData.title.trim()) {
            errors.push(`Row ${i + 2}: Title is required`);
            continue;
          }

          await addDoc(collection(db, "codingTests"), testData);
          added++;
        } catch (err) {
          errors.push(`Row ${i + 2}: ${err.message}`);
        }
      }

      if (added > 0) {
        setCsvUploadStatus({
          message: `Successfully imported ${added} code question(s). ${errors.length > 0 ? `Failed: ${errors.length}` : ""}`,
          type: "success",
          errors: errors.length > 0 ? errors : null
        });
        fetchCodeQuestions();
        setTimeout(() => {
          setShowCodeQuestionBulkUploadModal(false);
          setCsvFile(null);
          setCsvUploadStatus({ message: "", type: "" });
        }, 3000);
      } else {
        setCsvUploadStatus({
          message: "Failed to import code questions",
          type: "error",
          errors: errors
        });
      }
    } catch (error) {
      console.error("Error importing bulk code questions:", error);
      setCsvUploadStatus({ message: "Error importing tests", type: "error" });
    }
  };

  // ==================== CODE QUESTIONS ZIP BUNDLE IMPORT FUNCTIONS ====================

  const handleCodeQuestionZipFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCodeQuestionZipFile(file);
      setCodeQuestionZipStatus({ message: "", type: "", details: [] });
    }
  };

  const downloadCodeQuestionZipTemplate = async () => {
    try {
      const zip = new JSZip();

      const headers = [
        "Title",
        "Description",
        "Module Name",
        "Category",
        "Difficulty",
        "Total Marks",
        "Pass Marks",
        "Order",
        "Question Image URL",
        "Is Proctored (true/false)",
        "Is Published (true/false)",
        "Initial Code",
        "Hints (separated by |)",
        "Solution",
        "Test Cases"
      ];

      const sampleRow = [
        "4-bit Adder Design",
        "Implement a 4-bit adder module in Verilog. Refer to the circuit diagram below:\n{{img:adder_schematic.png}}",
        "Digital Design",
        "Verilog",
        "Medium",
        "100",
        "40",
        "1",
        "",
        "false",
        "true",
        "module adder(input [3:0] a, b, output [3:0] sum);\n  // your code here\nendmodule",
        "Use assign statement | Check output range",
        "module adder(input [3:0] a, b, output [3:0] sum);\n  assign sum = a + b;\nendmodule",
        "Test Cases Description: Input a=4, b=5 => sum=9; Input a=15, b=1 => sum=0 (overflow)"
      ];

      const csvContent = [
        headers.join(","),
        sampleRow.map(cell => `"${cell.replace(/"/g, '""')}"`).join(",")
      ].join("\n");

      zip.file("code_questions.csv", csvContent);

      const pngData = getDummyPngBase64();
      const pngBytes = Uint8Array.from(atob(pngData), c => c.charCodeAt(0));
      zip.folder("images").file("adder_schematic.png", pngBytes);

      zip.file("README.txt",
        `HOW TO USE THIS TEMPLATE FOR BULK CODING QUESTIONS
===================================================

1. Edit code_questions.csv with your coding problem details.
2. Use {{img:filename.ext}} placeholders in the "Description" column where you want images to appear in the problem statement.
3. Put the real images in the images/ folder.
   - Image filenames must match exactly what you wrote in {{img:...}} placeholders.
4. Keep this ZIP structure:
   code_questions_import.zip/
   ├── code_questions.csv
   └── images/
       ├── adder_schematic.png
       └── ...
5. Upload this ZIP using the "Import ZIP Bundle (with Images)" panel in the bulk upload modal.
`);

      const content = await zip.generateAsync({ type: "blob" });
      const url = window.URL.createObjectURL(content);
      const a = document.createElement("a");
      a.href = url;
      a.download = "code_questions_import_template.zip";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error generating code questions ZIP template:", err);
    }
  };

  const uploadCodeQuestionZipImages = async (imageFilesMap, categoryFolder) => {
    const imageUrlMap = {};
    const filenames = Object.keys(imageFilesMap);
    let uploadedCount = 0;

    for (const filename of filenames) {
      try {
        setCodeQuestionZipProgress(prev => ({
          ...prev,
          phase: "Uploading Images",
          current: uploadedCount + 1,
          total: filenames.length
        }));

        const fileData = await imageFilesMap[filename].async("arraybuffer");
        const ext = filename.split(".").pop().toLowerCase();
        const mimeTypes = {
          png: "image/png",
          jpg: "image/jpeg",
          jpeg: "image/jpeg",
          gif: "image/gif",
          webp: "image/webp",
          svg: "image/svg+xml",
        };
        const contentType = mimeTypes[ext] || "image/png";

        const timestamp = Date.now();
        const safeFilename = filename.replace(/\s+/g, "_").replace(/[^a-zA-Z0-9._-]/g, "");
        const storagePath = `code_questions/${categoryFolder}/${timestamp}_${safeFilename}`;
        const storageRef = ref(storage, storagePath);

        const blob = new Blob([fileData], { type: contentType });
        const uploadTask = uploadBytesResumable(storageRef, blob, { contentType });

        await new Promise((resolve, reject) => {
          uploadTask.on(
            "state_changed",
            null,
            (error) => {
              console.error(`Firebase Storage upload error for ${filename}:`, error.code, error.message);
              let userMsg = error.message;
              if (error.code === 'storage/unauthorized') {
                userMsg = `Permission denied uploading image "${filename}". Make sure storage rules allow admin uploads to code_questions/.`;
              }
              reject(new Error(userMsg));
            },
            async () => {
              const downloadUrl = await getDownloadURL(storageRef);
              imageUrlMap[filename] = downloadUrl;
              resolve();
            }
          );
        });

        uploadedCount++;
      } catch (err) {
        console.error(`Failed to upload image "${filename}":`, err);
        imageUrlMap[filename] = null;
      }
    }

    return imageUrlMap;
  };

  const processCodeQuestionZipBundle = async () => {
    if (!codeQuestionZipFile) {
      setCodeQuestionZipStatus({
        message: "Please select a ZIP bundle file.",
        type: "error",
        details: []
      });
      return;
    }

    if (!auth.currentUser) {
      setCodeQuestionZipStatus({
        message: "You must be logged in to import coding questions with images.",
        type: "error",
        details: []
      });
      return;
    }

    setCodeQuestionZipImporting(true);
    setCodeQuestionZipStatus({ message: "", type: "", details: [] });

    try {
      setCodeQuestionZipProgress({ phase: "Extracting ZIP", current: 0, total: 0 });
      const zip = await JSZip.loadAsync(codeQuestionZipFile);

      // Find code_questions.csv or questions.csv
      let csvFile = null;
      zip.forEach((relativePath, file) => {
        if (!file.dir && (relativePath.toLowerCase().endsWith("code_questions.csv") || relativePath.toLowerCase().endsWith("questions.csv"))) {
          csvFile = file;
        }
      });

      if (!csvFile) {
        setCodeQuestionZipImporting(false);
        setCodeQuestionZipStatus({
          message: "No CSV file found in the ZIP bundle.",
          type: "error",
          details: ["Make sure your ZIP contains a file named 'code_questions.csv' or 'questions.csv' in the root or a subfolder."]
        });
        return;
      }

      // Extract CSV text
      const csvText = await csvFile.async("text");
      const parsedRows = parseCSV(csvText);

      if (parsedRows.length < 2) {
        setCodeQuestionZipImporting(false);
        setCodeQuestionZipStatus({
          message: "CSV file is empty or invalid.",
          type: "error",
          details: ["Make sure the CSV has a header row and at least one question row."]
        });
        return;
      }

      // Find all image files
      const imageFilesMap = {};
      zip.forEach((relativePath, file) => {
        if (!file.dir) {
          const lowerPath = relativePath.toLowerCase();
          const isImage = /\.(png|jpg|jpeg|gif|webp|svg)$/.test(lowerPath);
          if (isImage) {
            const filename = relativePath.split("/").pop();
            imageFilesMap[filename] = file;
          }
        }
      });

      const imageCount = Object.keys(imageFilesMap).length;

      // Upload images
      let imageUrlMap = {};
      if (imageCount > 0) {
        imageUrlMap = await uploadCodeQuestionZipImages(imageFilesMap, "bulk_zip");
      }

      // Save code questions
      const dataRows = parsedRows.slice(1);
      setCodeQuestionZipProgress({ phase: "Saving Questions", current: 0, total: dataRows.length });
      let successCount = 0;
      const errors = [];

      for (let i = 0; i < dataRows.length; i++) {
        const row = dataRows[i];

        if (row.length < 2) {
          errors.push(`Row ${i + 2}: Insufficient columns (must have at least Title and Description)`);
          continue;
        }

        const title = row[0]?.trim();
        const description = row[1]?.trim();

        if (!title) {
          errors.push(`Row ${i + 2}: Title is required`);
          continue;
        }

        try {
          const resolvedDescription = resolveQuestionImagePlaceholders(description || "", imageUrlMap);

          let resolvedQuestionIMG = row[8]?.trim() || "";
          if (resolvedQuestionIMG.startsWith("{{img:") && resolvedQuestionIMG.endsWith("}}")) {
            const filename = resolvedQuestionIMG.substring(6, resolvedQuestionIMG.length - 2).trim();
            if (imageUrlMap[filename]) {
              resolvedQuestionIMG = imageUrlMap[filename];
            }
          } else if (imageUrlMap[resolvedQuestionIMG]) {
            resolvedQuestionIMG = imageUrlMap[resolvedQuestionIMG];
          }

          const payload = {
            title: title,
            description: resolvedDescription,
            courseName: row[2]?.trim() || "",
            category: row[3]?.trim() || "Verilog",
            difficulty: row[4]?.trim() || "Medium",
            totalMarks: Number(row[5]) || 100,
            passMarks: Number(row[6]) || 40,
            order: Number(row[7]) || 1,
            questionIMG: resolvedQuestionIMG,
            isProctored: row[9]?.toLowerCase() === "true",
            isPublished: row[10]?.toLowerCase() === "true",
            initialCode: row[11] || "",
            hints: row[12]?.split("|").map(h => h.trim()).filter(h => h) || [""],
            solution: row[13] || "",
            testCases: row[14] || "",
            createdAt: new Date(),
            updatedAt: new Date()
          };

          await addDoc(collection(db, "codingTests"), payload);
          successCount++;
        } catch (err) {
          errors.push(`Row ${i + 2}: ${err.message}`);
        }
      }

      // Finish
      setCodeQuestionZipImporting(false);
      setCodeQuestionZipProgress({ phase: "Done", current: successCount, total: dataRows.length });

      if (errors.length > 0) {
        setCodeQuestionZipStatus({
          message: `Imported ${successCount} of ${dataRows.length} coding questions with errors.`,
          type: "warning",
          details: errors
        });
      } else {
        setCodeQuestionZipStatus({
          message: `Successfully imported all ${successCount} coding questions with ${imageCount} images! 🎉`,
          type: "success",
          details: []
        });
      }

      fetchCodeQuestions();

      // Clean up after some time
      setTimeout(() => {
        setShowCodeQuestionBulkUploadModal(false);
        setCodeQuestionZipFile(null);
        setCodeQuestionZipStatus({ message: "", type: "", details: [] });
      }, 5000);

    } catch (err) {
      console.error("ZIP coding questions import error:", err);
      setCodeQuestionZipImporting(false);
      setCodeQuestionZipStatus({
        message: `Import failed: ${err.message}`,
        type: "error",
        details: []
      });
    }
  };




  // Results
  const fetchResults = async () => {
    const snapshot = await getDocs(collection(db, "testResults"));
    const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    setResults(data);
  };

  const filteredResults = results.filter((r) => {
    if (resultsFilter === "passed") return r.score >= r.passMarks;
    if (resultsFilter === "failed") return r.score < r.passMarks;
    return true;
  });

  if (loading) {
    return (
      <div className={styles.container}>
        <p>Loading test data...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <PageHeader
        icon={CheckSquare}
        title="Test & Assessment Management"
        subtitle="Schedule coding tests, module quizzes, and review student performance results from one place."
        badge={`${tests.length + codeQuestions.length} Assessments`}
      />

      <div className={styles.tabRow}>
        <button
          type="button"
          className={`${styles.tabButton} ${activeSubTab === "addCodeQuestions" ? styles.tabButtonActive : ""
            }`}
          onClick={() => setActiveSubTab("addCodeQuestions")}
        >
          <Code size={14} />
          Coding Practice
        </button>
        <button
          type="button"
          className={`${styles.tabButton} ${activeSubTab === "addModuleTests" ? styles.tabButtonActive : ""
            }`}
          onClick={() => setActiveSubTab("addModuleTests")}
        >
          <Calendar size={14} />
          Module Tests
        </button>


        <button
          type="button"
          className={`${styles.tabButton} ${activeSubTab === "results" ? styles.tabButtonActive : ""
            }`}
          onClick={() => setActiveSubTab("results")}
        >
          <CheckSquare size={14} />
          Results
        </button>
      </div>

      {/* ========== SCHEDULE CODE QUESTIONS TAB ========== */}
      {activeSubTab === "addCodeQuestions" && (
        <>
          {/* Debug banner removed */}
          <div className={styles.card}>
            {/* Stats Row */}
            <div className={styles.statsRow}>
              <div className={styles.statCard}>
                <div className={styles.statNumber}>{codeQuestions.length}</div>
                <div className={styles.statLabel}>Total Code Questions</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statNumber}>
                  {codeQuestions.filter(t => t.isPublished).length}
                </div>
                <div className={styles.statLabel}>Published</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statNumber}>
                  {codeQuestions.filter(t => !t.isPublished).length}
                </div>
                <div className={styles.statLabel}>Draft</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statNumber}>
                  {filteredCodeQuestions.length}
                </div>
                <div className={styles.statLabel}>Filtered Results</div>
              </div>
            </div>

            {/* Filters */}
            <div className={styles.filters}>
              <div className={styles.searchBox}>
                <Search size={16} className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="Search code questions..."
                  value={codeQuestionSearch}
                  onChange={(e) => setCodeQuestionSearch(e.target.value)}
                  className={styles.searchInput}
                />
              </div>

              <button
                type="button"
                className={styles.filterSelect}
                style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', cursor: 'pointer' }}
                onClick={() => setCodeQuestionSortOrder(prev => prev === "asc" ? "desc" : "asc")}
                title="Toggle Sort Order"
              >
                {codeQuestionSortOrder === "asc" ? (
                  <>
                    <ArrowUp size={14} />
                    <span>Order: Asc (1→9)</span>
                  </>
                ) : (
                  <>
                    <ArrowDown size={14} />
                    <span>Order: Desc (9→1)</span>
                  </>
                )}
              </button>

              <select
                value={codeQuestionLanguageFilter}
                onChange={(e) => setCodeQuestionLanguageFilter(e.target.value)}
                className={styles.filterSelect}
              >
                <option value="all">All Languages</option>
                {codingLanguages.map(lang => (
                  <option key={lang} value={lang}>{lang}</option>
                ))}
              </select>

              <select
                value={codeQuestionDifficultyFilter}
                onChange={(e) => setCodeQuestionDifficultyFilter(e.target.value)}
                className={styles.filterSelect}
              >
                <option value="all">All Difficulties</option>
                <option value="Easy">Easy</option>
                <option value="Medium">Medium</option>
                <option value="Advanced">Advanced</option>
              </select>

              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={() => {
                  setCsvFile(null);
                  setCsvUploadStatus({ message: "", type: "" });
                  setShowCodeQuestionBulkUploadModal(true);
                }}
              >
                <Upload size={14} />
                Upload Questions
              </button>

              <button
                type="button"
                className={styles.primaryBtn}
                onClick={() => {
                  resetCodeQuestionForm();
                  setShowCodeQuestionModal(true);
                }}
              >
                <Plus size={14} />
                Add Code Question
              </button>
            </div>

            {/* Tests List */}
            {filteredCodeQuestions.length === 0 ? (
              <div className={styles.emptyState}>
                <p>No code questions found. Add your first code question.</p>
              </div>
            ) : (
              <div className={styles.testsList}>
                {filteredCodeQuestions.map(test => {
                  const status = getTestStatusBadge(test);
                  return (
                    <div key={test.id} className={styles.testCard}>
                      <div className={styles.testHeader}>
                        <div>
                          <div className={styles.testTitle}>
                            {test.order !== undefined && test.order !== null && test.order !== "" ? `${test.order}. ` : ""}{test.title}
                          </div>
                          <div className={styles.badgeRow}>
                            <span className="badge bg-primary">{test.category}</span>
                            <span className={`badge ${test.difficulty === 'Easy' ? 'bg-success' :
                              test.difficulty === 'Hard' ? 'bg-danger' : 'bg-warning'
                              }`}>{test.difficulty}</span>
                            <span className={`badge ${status.className}`}>
                              {status.text}
                            </span>
                            {test.isProctored && (
                              <span className="badge bg-dark">Proctored</span>
                            )}
                            <span className="badge bg-info">
                              Total: {test.totalMarks || 100} marks
                            </span>
                            <span className="badge bg-secondary">
                              Pass: {test.passMarks || 40} marks
                            </span>
                          </div>
                        </div>
                        <div className={styles.testActions}>
                          <button
                            type="button"
                            className={styles.actionBtn}
                            onClick={() => handleEditCodeQuestion(test)}
                            title="View & Manage Questions"
                          >
                            <Eye size={14} />
                          </button>
                          <button
                            type="button"
                            className={styles.actionBtn}
                            onClick={() => handleEditCodeQuestion(test)}
                            title="Edit question"
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            type="button"
                            className={`${styles.actionBtn} ${styles.deleteBtn}`}
                            onClick={() => handleDeleteCodeQuestion(test)}
                            title="Delete question"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>



                      {test.description && (
                        <div style={{ fontSize: "0.8rem", color: "#6c757d", marginTop: '0.25rem' }}>
                          {(() => {
                            const plainText = test.description.replace(/<[^>]*>?/gm, '');
                            return plainText.length > 150 ? plainText.substring(0, 150) + "..." : plainText;
                          })()}
                        </div>
                      )}

                      <div className={styles.testMetaRow} style={{ marginTop: '0.5rem', borderTop: '1px solid #f0f0f0', paddingTop: '0.4rem' }}>
                        <span>
                          💡 {test.hints?.length || 0} Hints
                        </span>
                        <span>
                          ✅ {test.solution ? 'Has Soln' : 'No Soln'}
                        </span>
                        <span>
                          📝 {test.testCases ? 'Has Cases' : 'No Cases'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* ========== SCHEDULE QUIZZ TESTS TAB ========== */}
      {activeSubTab === "addModuleTests" && (
        <>
          <div className={styles.card}>
            <div className={styles.statsRow}>
              <div className={styles.statCard}>
                <div className={styles.statNumber}>{tests.length}</div>
                <div className={styles.statLabel}>Total Tests</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statNumber}>
                  {tests.filter((t) => getTestStatusBadge(t).text === "Upcoming").length}
                </div>
                <div className={styles.statLabel}>Upcoming</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statNumber}>
                  {tests.filter((t) => t.isPublished).length}
                </div>
                <div className={styles.statLabel}>Published</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statNumber}>
                  {tests.filter((t) => t.isProctored).length}
                </div>
                <div className={styles.statLabel}>Proctored</div>
              </div>
            </div>

            <div className={styles.filters}>
              <div className={styles.searchBox}>
                <Search size={16} className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="Search tests..."
                  value={testSearch}
                  onChange={(e) => setTestSearch(e.target.value)}
                  className={styles.searchInput}
                />
              </div>
              <select
                value={testStatusFilter}
                onChange={(e) => setTestStatusFilter(e.target.value)}
                className={styles.filterSelect}
              >
                <option value="all">All Status</option>
                <option value="upcoming">Upcoming</option>
                <option value="past">Past</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
              </select>
              <select
                value={testTypeFilter}
                onChange={(e) => {
                  setTestTypeFilter(e.target.value);
                  setTestCategoryFilter("all");
                }}
                className={styles.filterSelect}
              >
                <option value="all">All Types</option>
                <option value="quiz">Quiz</option>
                <option value="module-test">Module Test</option>
              </select>
              <select
                value={testCategoryFilter}
                onChange={(e) => setTestCategoryFilter(e.target.value)}
                className={styles.filterSelect}
              >
                <option value="all">All Categories</option>
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>

              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={() => {
                  setCsvFile(null);
                  setCsvUploadStatus({ message: "", type: "" });
                  setShowBulkUploadModal(true);
                }}
                style={{ marginRight: '0.5rem' }}
              >
                <Upload size={14} />
                Upload Tests
              </button>
              <button
                type="button"
                className={styles.primaryBtn}
                onClick={() => {
                  setEditingTest(null);
                  resetTestForm();
                  setShowTestModal(true);
                }}
              >
                <Plus size={14} />
                Add Test
              </button>
            </div>

            {filteredTests.length === 0 ? (
              <div className={styles.emptyState}>
                <p>No tests found. Add your first test.</p>
              </div>
            ) : (
              <div className={styles.testsList}>
                {filteredTests.map((test) => {
                  const status = getTestStatusBadge(test);
                  return (
                    <div key={test.id} className={styles.testCard}>
                      <div className={styles.testHeader}>
                        <div>
                          <div className={styles.testTitle}>{test.title}</div>
                          <div className={styles.badgeRow}>
                            <span className={`badge bg-primary`}>
                              {test.type}
                            </span>
                            {test.category && (
                              <span className={`badge bg-success`}>{test.category}</span>
                            )}
                            {test.courseName && (
                              <span className={`badge bg-info`}>{test.courseName}</span>
                            )}
                            <span className={`badge ${status.className}`}>
                              {status.text}
                            </span>
                            {test.isProctored && (
                              <span className="badge bg-dark">Proctored</span>
                            )}
                          </div>
                        </div>
                        <div className={styles.testActions}>
                          <button
                            type="button"
                            className={styles.actionBtn}
                            onClick={() => handleViewTest(test)}
                            title="View test & manage questions"
                          >
                            <Eye size={14} />
                          </button>
                          <button
                            type="button"
                            className={styles.actionBtn}
                            onClick={() => handleEditTest(test)}
                            title="Edit test"
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            type="button"
                            className={`${styles.actionBtn} ${styles.deleteBtn}`}
                            onClick={() => handleDeleteTest(test)}
                            title="Delete test"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>

                      <div className={styles.testMetaRow}>
                        <span>
                          <Clock size={12} className="me-1" />
                          {test.scheduledAt
                            ? `${test.scheduledAt.toLocaleDateString()} ${test.scheduledAt.toLocaleTimeString(
                              [],
                              { hour: "2-digit", minute: "2-digit" }
                            )}`
                            : "Not scheduled"}
                        </span>
                        {test.duration && <span>Duration: {test.duration}</span>}
                        {test.totalMarks !== undefined && (
                          <span>Total: {test.totalMarks} marks</span>
                        )}
                        {test.passMarks !== undefined && (
                          <span>Pass: {test.passMarks} marks</span>
                        )}
                        <span>Attempts: {test.attemptsAllowed || 1}</span>
                        {test.targetGroup && (
                          <span>
                            <Users size={12} className="me-1" />
                            {test.targetGroup}
                          </span>
                        )}
                      </div>

                      {test.description && (
                        <div style={{ fontSize: "0.8rem", color: "#6c757d" }}>
                          {test.description}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {showTestModal && (
            <div
              className={styles.modalOverlay}
              onClick={() => setShowTestModal(false)}
            >
              <div
                className={styles.modal}
                onClick={(e) => e.stopPropagation()}
              >
                <div className={styles.modalHeader}>
                  <h5>{editingTest ? "Edit Test" : "Schedule New Test"}</h5>
                  <button
                    type="button"
                    className={styles.closeBtn}
                    onClick={() => setShowTestModal(false)}
                  >
                    <X size={18} />
                  </button>
                </div>
                <form onSubmit={handleTestSubmit} className={styles.modalBody}>
                  <div className={styles.formGrid}>
                    <div className={styles.formGroup}>
                      <label>Test Title *</label>
                      <input
                        type="text"
                        name="title"
                        value={testForm.title}
                        onChange={handleTestInputChange}
                        required
                        className={styles.formInput}
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label>Module</label>
                      <input
                        type="text"
                        name="courseName"
                        value={testForm.courseName}
                        onChange={handleTestInputChange}
                        className={styles.formInput}
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label>Test Type *</label>
                      <select
                        name="type"
                        value={testForm.type}
                        onChange={handleTestInputChange}
                        className={styles.formSelect}
                      >
                        <option value="quiz">Quiz</option>
                        <option value="module-test">Module Test</option>
                      </select>
                    </div>

                    <div className={styles.formGroup}>
                      <label>Category *</label>
                      <select
                        name="category"
                        value={testForm.category}
                        onChange={handleTestInputChange}
                        className={styles.formSelect}
                      >
                        <option value="">Select Category</option>
                        {categories.map((cat) => (
                          <option key={cat} value={cat}>
                            {cat}
                          </option>
                        ))}
                      </select>
                    </div>


                    <div className={styles.formGroup}>
                      <label>Date</label>
                      <input
                        type="date"
                        name="date"
                        value={testForm.date}
                        onChange={handleTestInputChange}
                        className={styles.formInput}
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label>Time</label>
                      <input
                        type="time"
                        name="time"
                        value={testForm.time}
                        onChange={handleTestInputChange}
                        className={styles.formInput}
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label>Duration</label>
                      <input
                        type="text"
                        name="duration"
                        value={testForm.duration}
                        onChange={handleTestInputChange}
                        placeholder="e.g., 60 minutes"
                        className={styles.formInput}
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label>Total Marks</label>
                      <input
                        type="number"
                        name="totalMarks"
                        value={testForm.totalMarks}
                        onChange={handleTestInputChange}
                        className={styles.formInput}
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label>Pass Marks</label>
                      <input
                        type="number"
                        name="passMarks"
                        value={testForm.passMarks}
                        onChange={handleTestInputChange}
                        className={styles.formInput}
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label>Attempts Allowed</label>
                      <input
                        type="number"
                        name="attemptsAllowed"
                        value={testForm.attemptsAllowed}
                        onChange={handleTestInputChange}
                        min={1}
                        className={styles.formInput}
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label>Target Group</label>
                      <input
                        type="text"
                        name="targetGroup"
                        value={testForm.targetGroup}
                        onChange={handleTestInputChange}
                        placeholder="e.g., VLSI Batch Aug 2024"
                        className={styles.formInput}
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label>Silicon-Point Value</label>
                      <input
                        type="number"
                        min="0"
                        name="siliconPoints"
                        value={testForm.siliconPoints}
                        onChange={handleTestInputChange}
                        placeholder="50"
                        className={styles.formInput}
                      />
                    </div>
                  </div>

                  <div className={styles.formGroup}>
                    <label>Description</label>
                    <textarea
                      name="description"
                      value={testForm.description}
                      onChange={handleTestInputChange}
                      className={styles.formTextarea}
                    />
                  </div>

                  <div className={styles.formGrid}>
                    <div className={styles.formGroup}>
                      <label className={styles.checkboxLabel}>
                        <input
                          type="checkbox"
                          name="isProctored"
                          checked={testForm.isProctored}
                          onChange={handleTestInputChange}
                          className={styles.checkbox}
                        />
                        <span>Proctored Test</span>
                      </label>
                    </div>
                    <div className={styles.formGroup}>
                      <label className={styles.checkboxLabel}>
                        <input
                          type="checkbox"
                          name="isPublished"
                          checked={testForm.isPublished}
                          onChange={handleTestInputChange}
                          className={styles.checkbox}
                        />
                        <span>Publish Test</span>
                      </label>
                    </div>
                  </div>

                  <div className={styles.modalActions}>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowTestModal(false)}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">
                      <Save size={14} className="me-1" />
                      {editingTest ? "Update Test" : "Create Test"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Bulk Upload Modal */}
          {showBulkUploadModal && (
            <div className={styles.modalOverlay} onClick={() => setShowBulkUploadModal(false)}>
              <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                  <h5>Upload Tests (CSV)</h5>
                  <button
                    type="button"
                    className={styles.closeBtn}
                    onClick={() => setShowBulkUploadModal(false)}
                  >
                    <X size={18} />
                  </button>
                </div>

                <div className={styles.modalBody}>
                  <p>Upload a CSV file containing multiple test rows. Use the template to ensure correct headers.</p>

                  <div className={styles.formGroup}>
                    <button
                      type="button"
                      className="btn btn-outline-primary me-2 mb-2"
                      onClick={downloadTestCSVTemplate}
                    >
                      <Download size={16} className="me-2" />
                      Download Template
                    </button>
                    <div className="mb-3">
                      <input
                        type="file"
                        accept=".csv"
                        onChange={handleCSVUpload}
                        className="form-control"
                      />
                    </div>

                    {csvFile && (
                      <div className="alert alert-info">
                        <strong>Selected:</strong> {csvFile.name}
                      </div>
                    )}

                    {csvUploadStatus.message && (
                      <div className={`alert alert-${csvUploadStatus.type === 'error' ? 'danger' : csvUploadStatus.type === 'info' ? 'info' : 'success'}`}>
                        {csvUploadStatus.message}
                        {csvUploadStatus.errors && csvUploadStatus.errors.length > 0 && (
                          <details className="mt-2">
                            <summary style={{ cursor: 'pointer' }}>View errors ({csvUploadStatus.errors.length})</summary>
                            <ul className="mt-2 mb-0">
                              {csvUploadStatus.errors.map((err, idx) => (
                                <li key={idx}>{err}</li>
                              ))}
                            </ul>
                          </details>
                        )}
                      </div>
                    )}

                    <div className={styles.modalActions}>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setShowBulkUploadModal(false)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-success"
                        onClick={processBulkTestCSV}
                        disabled={!csvFile}
                      >
                        <Upload size={16} className="me-2" />
                        Import Tests
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}



          {showViewTestModal && viewingTest && (
            <div
              className={styles.modalOverlay}
              onClick={() => {
                setShowViewTestModal(false);
                setViewingTest(null);
                setTestQuestions([]);
                setShowQuestionForm(false);
              }}
            >
              <div
                className={`${styles.modal} ${styles.largeModal}`}
                onClick={(e) => e.stopPropagation()}
              >
                <div className={styles.modalHeader}>
                  <div>
                    <h5>{viewingTest.title}</h5>
                    <p style={{ fontSize: "0.85rem", color: "#6c757d", margin: 0 }}>
                      Manage questions and answers for this test
                    </p>
                  </div>
                  <button
                    type="button"
                    className={styles.closeBtn}
                    onClick={() => {
                      setShowViewTestModal(false);
                      setViewingTest(null);
                      setTestQuestions([]);
                      setShowQuestionForm(false);
                    }}
                  >
                    <X size={18} />
                  </button>
                </div>

                <div className={styles.modalBody}>
                  <div className={styles.testInfoBar}>
                    <div className={styles.infoItem}>
                      <strong>Type:</strong> {viewingTest.type}
                    </div>
                    {viewingTest.category && (
                      <div className={styles.infoItem}>
                        <strong>Category:</strong> {viewingTest.category}
                      </div>
                    )}
                    {viewingTest.totalMarks !== undefined && (
                      <div className={styles.infoItem}>
                        <strong>Total Marks:</strong> {viewingTest.totalMarks}
                      </div>
                    )}
                    <div className={styles.infoItem}>
                      <strong>Questions:</strong> {testQuestions.length}
                    </div>
                    <div className={styles.infoItem}>
                      <strong>Calculated Marks:</strong> {calculateTotalMarks()}
                    </div>
                  </div>

                  <div className={styles.questionManagementSection}>
                    <div className={styles.sectionHeader}>
                      <h6>Test Questions</h6>
                      <div className={styles.headerActions}>
                        <button
                          type="button"
                          className={styles.secondaryBtn}
                          onClick={() => {
                            setShowQuestionZipImport(!showQuestionZipImport);
                            setShowCSVUpload(false);
                          }}
                          style={{ background: '#f0f4ff', color: '#3b82f6', borderColor: '#3b82f6', marginRight: '0.5rem' }}
                        >
                          <Upload size={14} />
                          Import Bundle (ZIP)
                        </button>
                        <button
                          type="button"
                          className={styles.secondaryBtn}
                          onClick={() => {
                            setShowCSVUpload(!showCSVUpload);
                            setShowQuestionZipImport(false);
                          }}
                        >
                          <Upload size={14} />
                          Import CSV
                        </button>
                        <button
                          type="button"
                          className={styles.primaryBtn}
                          onClick={handleAddQuestion}
                        >
                          <Plus size={14} />
                          Add Question
                        </button>
                      </div>
                    </div>

                    {showQuestionZipImport && (
                      <div className={styles.csvUploadCard} style={{ background: '#fbfcfe', border: '1px solid #d0e0fc' }}>
                        <div className={styles.csvUploadHeader}>
                          <h6 style={{ color: '#1e3a8a', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontSize: '1.1rem' }}>📦</span>
                            Import Questions + Images (ZIP Bundle)
                          </h6>
                          <button
                            type="button"
                            className={styles.downloadTemplateBtn}
                            onClick={downloadQuestionZipTemplate}
                            style={{ background: '#3b82f6', color: '#fff' }}
                          >
                            <Download size={14} />
                            Download Template ZIP
                          </button>
                        </div>
                        <div className={styles.csvInstructions} style={{ background: '#eff6ff', color: '#1e40af' }}>
                          <AlertCircle size={16} />
                          <p>
                            Upload a ZIP file containing: 1) <strong>questions.csv</strong> with your question text using <code>{"{{img:image_name.png}}"}</code> placeholders, and 2) an <strong>images/</strong> folder containing the corresponding image files.
                          </p>
                        </div>
                        <div className={styles.csvUploadArea} style={{ background: '#f3f4f6', border: '2px dashed #9ca3af' }}>
                          <input
                            type="file"
                            accept=".zip"
                            onChange={handleQuestionZipFileChange}
                            className={styles.csvFileInput}
                            id="questionZipFileInput"
                            disabled={questionZipImporting}
                          />
                          <label htmlFor="questionZipFileInput" className={styles.csvFileLabel} style={{ color: '#4b5563' }}>
                            {questionZipFile ? questionZipFile.name : "Choose ZIP bundle or drag here"}
                          </label>
                          {questionZipFile && (
                            <small style={{ display: 'block', color: '#6b7280', marginTop: '4px' }}>
                              Size: {(questionZipFile.size / 1024).toFixed(1)} KB
                            </small>
                          )}
                        </div>

                        {/* Progress Bar */}
                        {questionZipImporting && (
                          <div style={{ padding: '1rem 0' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                              <div className="spinner-border spinner-border-sm text-primary" role="status" />
                              <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#374151' }}>
                                {questionZipProgress.phase}
                                {questionZipProgress.total > 0 && ` (${questionZipProgress.current}/${questionZipProgress.total})`}
                              </span>
                            </div>
                            {questionZipProgress.total > 0 && (
                              <div style={{ background: '#e5e7eb', height: '8px', borderRadius: '4px', overflow: 'hidden' }}>
                                <div
                                  style={{
                                    background: '#3b82f6',
                                    width: `${Math.round((questionZipProgress.current / questionZipProgress.total) * 100)}%`,
                                    height: '100%'
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        )}

                        {/* Status Message */}
                        {questionZipStatus.message && (
                          <div className={`alert alert-${questionZipStatus.type === "success" ? "success"
                              : questionZipStatus.type === "warning" ? "warning"
                                : "danger"
                            } alert-dismissible fade show`} style={{ margin: '1rem 0' }}>
                            {questionZipStatus.message}
                            {questionZipStatus.details.length > 0 && (
                              <details style={{ marginTop: "0.5rem" }}>
                                <summary style={{ cursor: 'pointer', fontWeight: 600 }}>View details ({questionZipStatus.details.length})</summary>
                                <ul style={{ marginBottom: 0, marginTop: "0.5rem", maxHeight: '150px', overflowY: 'auto' }}>
                                  {questionZipStatus.details.map((d, idx) => (
                                    <li key={idx} style={{ fontSize: '0.85rem' }}>{d}</li>
                                  ))}
                                </ul>
                              </details>
                            )}
                          </div>
                        )}

                        <div className={styles.csvUploadActions}>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            disabled={questionZipImporting}
                            onClick={() => {
                              setShowQuestionZipImport(false);
                              setQuestionZipFile(null);
                              setQuestionZipStatus({ message: "", type: "", details: [] });
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={processQuestionZipBundle}
                            disabled={!questionZipFile || questionZipImporting}
                            style={{ background: '#2563eb', borderColor: '#2563eb' }}
                          >
                            {questionZipImporting ? "Importing..." : "Import ZIP Bundle"}
                          </button>
                        </div>
                      </div>
                    )}

                    {showCSVUpload && (
                      <div className={styles.csvUploadCard}>
                        <div className={styles.csvUploadHeader}>
                          <h6>Import Questions from CSV</h6>
                          <button
                            type="button"
                            className={styles.downloadTemplateBtn}
                            onClick={downloadCSVTemplate}
                          >
                            <Download size={14} />
                            Download Template
                          </button>
                        </div>
                        <div className={styles.csvInstructions}>
                          <AlertCircle size={16} />
                          <p>
                            Upload a CSV file with the following columns: Question Text, Option A, Option B,
                            Option C, Option D, Correct Option (A/B/C/D), Marks, Explanation (optional),
                            Image URL (optional). Download the template for reference.
                          </p>
                        </div>
                        <div className={styles.csvUploadArea}>
                          <input
                            type="file"
                            accept=".csv"
                            onChange={handleCSVUpload}
                            className={styles.csvFileInput}
                            id="csvFileInput"
                          />
                          <label htmlFor="csvFileInput" className={styles.csvFileLabel}>
                            {csvFile ? csvFile.name : "Choose CSV file or drag here"}
                          </label>
                        </div>
                        {csvUploadStatus.message && (
                          <div className={`${styles.uploadStatus} ${styles[csvUploadStatus.type]}`}>
                            {csvUploadStatus.message}
                            {csvUploadStatus.errors && csvUploadStatus.errors.length > 0 && (
                              <details className={styles.errorDetails}>
                                <summary>View errors ({csvUploadStatus.errors.length})</summary>
                                <ul>
                                  {csvUploadStatus.errors.map((err, idx) => (
                                    <li key={idx}>{err}</li>
                                  ))}
                                </ul>
                              </details>
                            )}
                          </div>
                        )}
                        <div className={styles.csvUploadActions}>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => {
                              setShowCSVUpload(false);
                              setCsvFile(null);
                              setCsvUploadStatus({ message: "", type: "" });
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={processCSVFile}
                            disabled={!csvFile}
                          >
                            <Upload size={14} className="me-1" />
                            Import Questions
                          </button>
                        </div>
                      </div>
                    )}

                    {showQuestionForm && (
                      <div className={styles.questionFormCard}>
                        <div className={styles.formHeader}>
                          <h6>{editingQuestion ? "Edit Question" : "New Question"}</h6>
                          <button
                            type="button"
                            className={styles.closeBtn}
                            onClick={() => {
                              setShowQuestionForm(false);
                              resetTestQuestionForm();
                            }}
                          >
                            <X size={16} />
                          </button>
                        </div>
                        <form onSubmit={handleTestQuestionSubmit}>
                          <div className={styles.formGroup}>
                            <label>Question *</label>
                            <textarea
                              ref={testQuestionTextareaRef}
                              name="questionText"
                              value={testQuestionForm.questionText}
                              onChange={handleTestQuestionInputChange}
                              required
                              rows={3}
                              className={styles.formTextarea}
                              placeholder="Enter the question text..."
                            />
                          </div>

                          {/* Question Images Uploader & Custom Placement Panel */}
                          <div style={{
                            background: "#f8f9fa",
                            border: "1px dashed #dee2e6",
                            borderRadius: "8px",
                            padding: "1rem",
                            marginBottom: "1rem"
                          }}>
                            <div style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5rem",
                              fontWeight: "600",
                              color: "#495057",
                              marginBottom: "0.75rem"
                            }}>
                              <Image size={16} style={{ color: "#007bff" }} />
                              <span>Question Images (Upload & Insert at Cursor)</span>
                            </div>
                            <div style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.75rem",
                              flexWrap: "wrap",
                              marginBottom: "0.5rem"
                            }}>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleQuestionImageFileChange}
                                className="form-control form-control-sm"
                                style={{ maxWidth: "250px" }}
                              />
                              <button
                                type="button"
                                className="btn btn-outline-primary btn-sm"
                                onClick={uploadQuestionImageToFirebase}
                                disabled={!questionImageFile || uploadingQuestionImage}
                              >
                                {uploadingQuestionImage ? "Uploading..." : "Upload Image"}
                              </button>
                            </div>

                            {/* Progress Bar */}
                            {uploadingQuestionImage && (
                              <div style={{ display: "flex", alignItems: "center", gap: "8px", margin: "8px 0" }}>
                                <div style={{ flex: 1, background: "#e9ecef", height: "8px", borderRadius: "4px", overflow: "hidden" }}>
                                  <div style={{ background: "#007bff", width: `${questionImageProgress}%`, height: "100%" }} />
                                </div>
                                <span style={{ fontSize: "0.8rem", color: "#6c757d" }}>{questionImageProgress}%</span>
                              </div>
                            )}

                            {/* Success Badge */}
                            {questionImageSuccess && !uploadingQuestionImage && (
                              <div style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                background: "#d4edda",
                                border: "1px solid #c3e6cb",
                                color: "#155724",
                                borderRadius: "6px",
                                padding: "6px 12px",
                                fontSize: "0.85rem",
                                fontWeight: 600,
                                margin: "8px 0"
                              }}>
                                ✓ Image uploaded &amp; inserted successfully!
                              </div>
                            )}

                            {/* Error message */}
                            {questionImageError && (
                              <div className="text-danger small" style={{ margin: "8px 0" }}>
                                ⚠️ {questionImageError}
                              </div>
                            )}

                            {/* Grid of uploaded images to insert */}
                            {uploadedQuestionImages.length > 0 && (
                              <div style={{ marginTop: "0.75rem" }}>
                                <div className="text-muted small" style={{ marginBottom: "0.5rem" }}>
                                  Click any image to insert it at your cursor position:
                                </div>
                                <div style={{
                                  display: "grid",
                                  gridTemplateColumns: "repeat(auto-fill, minmax(60px, 1fr))",
                                  gap: "8px"
                                }}>
                                  {uploadedQuestionImages.map((url, idx) => (
                                    <div
                                      key={idx}
                                      onClick={() => insertQuestionImageAtCursor(url)}
                                      style={{
                                        position: "relative",
                                        aspectRatio: "1",
                                        border: "1px solid #dee2e6",
                                        borderRadius: "4px",
                                        overflow: "hidden",
                                        cursor: "pointer"
                                      }}
                                      title="Click to insert at cursor"
                                    >
                                      <img
                                        src={url}
                                        alt={`Uploaded ${idx}`}
                                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                      />
                                      <div style={{
                                        position: "absolute",
                                        inset: 0,
                                        background: "rgba(0, 0, 0, 0.4)",
                                        color: "#fff",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        opacity: 0,
                                        transition: "opacity 0.2s",
                                        fontSize: "0.7rem",
                                        fontWeight: "600"
                                      }}
                                        onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
                                        onMouseLeave={(e) => e.currentTarget.style.opacity = 0}
                                      >
                                        Insert
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>

                          <div className={styles.formGrid}>
                            <div className={styles.formGroup}>
                              <label>Option A *</label>
                              <input
                                type="text"
                                name="optionA"
                                value={testQuestionForm.optionA}
                                onChange={handleTestQuestionInputChange}
                                required
                                className={styles.formInput}
                              />
                            </div>
                            <div className={styles.formGroup}>
                              <label>Option B *</label>
                              <input
                                type="text"
                                name="optionB"
                                value={testQuestionForm.optionB}
                                onChange={handleTestQuestionInputChange}
                                required
                                className={styles.formInput}
                              />
                            </div>
                            <div className={styles.formGroup}>
                              <label>Option C</label>
                              <input
                                type="text"
                                name="optionC"
                                value={testQuestionForm.optionC}
                                onChange={handleTestQuestionInputChange}
                                className={styles.formInput}
                              />
                            </div>
                            <div className={styles.formGroup}>
                              <label>Option D</label>
                              <input
                                type="text"
                                name="optionD"
                                value={testQuestionForm.optionD}
                                onChange={handleTestQuestionInputChange}
                                className={styles.formInput}
                              />
                            </div>
                          </div>

                          <div className={styles.formGrid}>
                            <div className={styles.formGroup}>
                              <label>Correct Answer *</label>
                              <select
                                name="correctOption"
                                value={testQuestionForm.correctOption}
                                onChange={handleTestQuestionInputChange}
                                className={styles.formSelect}
                              >
                                <option value="A">A</option>
                                <option value="B">B</option>
                                <option value="C">C</option>
                                <option value="D">D</option>
                              </select>
                            </div>
                            <div className={styles.formGroup}>
                              <label>Marks *</label>
                              <input
                                type="number"
                                name="marks"
                                value={testQuestionForm.marks}
                                onChange={handleTestQuestionInputChange}
                                min={1}
                                required
                                className={styles.formInput}
                              />
                            </div>
                          </div>

                          <div className={styles.formGroup}>
                            <label>Explanation (Optional)</label>
                            <textarea
                              name="explanation"
                              value={testQuestionForm.explanation}
                              onChange={handleTestQuestionInputChange}
                              rows={2}
                              className={styles.formTextarea}
                              placeholder="Provide an explanation for the correct answer..."
                            />
                          </div>



                          <div className={styles.formActions}>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => {
                                setShowQuestionForm(false);
                                resetTestQuestionForm();
                              }}
                            >
                              Cancel
                            </button>
                            <button type="submit" className="btn btn-primary">
                              <Save size={14} className="me-1" />
                              {editingQuestion ? "Update Question" : "Add Question"}
                            </button>
                          </div>
                        </form>
                      </div>
                    )}

                    {testQuestions.length === 0 ? (
                      <div className={styles.emptyState}>
                        <p>No questions added yet. Click "Add Question" to get started.</p>
                      </div>
                    ) : (
                      <div className={styles.questionsList}>
                        {testQuestions.map((question, index) => (
                          <div key={question.id} className={styles.questionCard}>
                            <div className={styles.questionHeader}>
                              <div className={styles.questionNumber}>Q{index + 1}</div>
                              <div className={styles.questionActions}>
                                <span className="badge bg-info">{question.marks} mark{question.marks > 1 ? 's' : ''}</span>
                                <button
                                  type="button"
                                  className={styles.actionBtn}
                                  onClick={() => handleEditQuestion(question)}
                                  title="Edit question"
                                >
                                  <Edit size={14} />
                                </button>
                                <button
                                  type="button"
                                  className={`${styles.actionBtn} ${styles.deleteBtn}`}
                                  onClick={() => handleDeleteQuestion(question)}
                                  title="Delete question"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </div>
                            <div
                              className={styles.questionText}
                              dangerouslySetInnerHTML={{ __html: sanitizeHtml(question.questionText) }}
                            />
                            {question.imageUrl && (
                              <div className={styles.questionImage}>
                                <img src={question.imageUrl} alt="Question illustration" />
                              </div>
                            )}
                            <div className={styles.optionsList}>
                              {["A", "B", "C", "D"].map((opt) => (
                                question.options?.[opt] && (
                                  <div
                                    key={opt}
                                    className={`${styles.option} ${question.correctOption === opt ? styles.correctOption : ""
                                      }`}
                                  >
                                    <span className={styles.optionLabel}>{opt}.</span>
                                    <span className={styles.optionText}>{question.options[opt]}</span>
                                    {question.correctOption === opt && (
                                      <span className={styles.correctBadge}>✓ Correct</span>
                                    )}
                                  </div>
                                )
                              ))}
                            </div>
                            {question.explanation && (
                              <div className={styles.explanation}>
                                <strong>Explanation:</strong> {question.explanation}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}




      {/* ========== CODE QUESTION MODAL ========== */}
      {showCodeQuestionModal && (
        <div className={styles.modalOverlay} onClick={() => setShowCodeQuestionModal(false)}>
          <div className={`${styles.modal} ${styles.largeModal}`} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h5>{editingCodeQuestion ? "Edit" : "Add"} Code Question</h5>
              <button
                type="button"
                className={styles.closeBtn}
                onClick={() => setShowCodeQuestionModal(false)}
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleCodeQuestionSubmit} className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label>Problem Title *</label>
                <input
                  type="text"
                  name="title"
                  value={codeQuestionForm.title}
                  onChange={handleCodeQuestionInputChange}
                  required
                  className={styles.formInput}
                  placeholder="e.g., 4-bit Adder Design"
                />
              </div>

              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label>Module</label>
                  <input
                    type="text"
                    name="courseName"
                    value={codeQuestionForm.courseName}
                    onChange={handleCodeQuestionInputChange}
                    className={styles.formInput}
                    placeholder="e.g., Digital Design"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Language *</label>
                  <select
                    name="category"
                    value={codeQuestionForm.category}
                    onChange={handleCodeQuestionInputChange}
                    className={styles.formSelect}
                  >
                    {codingLanguages.map(lang => (
                      <option key={lang} value={lang}>{lang}</option>
                    ))}
                  </select>
                </div>
                <div className={styles.formGroup}>
                  <label>Difficulty *</label>
                  <select
                    name="difficulty"
                    value={codeQuestionForm.difficulty}
                    onChange={handleCodeQuestionInputChange}
                    className={styles.formSelect}
                  >
                    <option value="Easy">Easy</option>
                    <option value="Medium">Medium</option>
                    <option value="Advanced">Advanced</option>
                  </select>
                </div>
                <div className={styles.formGroup}>
                  <label>Total Marks *</label>
                  <input
                    type="number"
                    name="totalMarks"
                    value={codeQuestionForm.totalMarks}
                    onChange={handleCodeQuestionInputChange}
                    className={styles.formInput}
                    placeholder="e.g., 100"
                    min="1"
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Pass Marks *</label>
                  <input
                    type="number"
                    name="passMarks"
                    value={codeQuestionForm.passMarks}
                    onChange={handleCodeQuestionInputChange}
                    className={styles.formInput}
                    placeholder="e.g., 40"
                    min="1"
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Order *</label>
                  <input
                    type="number"
                    name="order"
                    value={codeQuestionForm.order}
                    onChange={handleCodeQuestionInputChange}
                    className={styles.formInput}
                    placeholder="e.g., 1"
                    min="1"
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Silicon-Point Value</label>
                  <input
                    type="number"
                    name="siliconPoints"
                    value={codeQuestionForm.siliconPoints}
                    onChange={handleCodeQuestionInputChange}
                    className={styles.formInput}
                    placeholder="50"
                    min="0"
                  />
                </div>
              </div>



              {/* Description */}
              <div className={styles.formGroup}>
                <label>Description *</label>
                <textarea
                  ref={codeQuestionTextareaRef}
                  name="description"
                  value={codeQuestionForm.description}
                  onChange={handleCodeQuestionInputChange}
                  rows={4}
                  required
                  className={styles.formTextarea}
                  placeholder="Describe the problem statement..."
                />
              </div>

              {/* Code Question Images Uploader & Custom Placement Panel */}
              <div style={{
                background: "#f8f9fa",
                border: "1px dashed #dee2e6",
                borderRadius: "8px",
                padding: "1rem",
                marginBottom: "1rem"
              }}>
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  fontWeight: "600",
                  color: "#495057",
                  marginBottom: "0.75rem"
                }}>
                  <Image size={16} style={{ color: "#28a745" }} />
                  <span>Problem Statement Images (Upload & Insert at Cursor)</span>
                </div>
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  flexWrap: "wrap",
                  marginBottom: "0.5rem"
                }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleCodeImageFileChange}
                    className="form-control form-control-sm"
                    style={{ maxWidth: "250px" }}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-success btn-sm"
                    onClick={uploadCodeImageToFirebase}
                    disabled={!codeImageFile || uploadingCodeImage}
                  >
                    {uploadingCodeImage ? "Uploading..." : "Upload Image"}
                  </button>
                </div>

                {/* Progress Bar */}
                {uploadingCodeImage && (
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", margin: "8px 0" }}>
                    <div style={{ flex: 1, background: "#e9ecef", height: "8px", borderRadius: "4px", overflow: "hidden" }}>
                      <div style={{ background: "#28a745", width: `${codeImageProgress}%`, height: "100%" }} />
                    </div>
                    <span style={{ fontSize: "0.8rem", color: "#6c757d" }}>{codeImageProgress}%</span>
                  </div>
                )}

                {/* Success Badge */}
                {codeImageSuccess && !uploadingCodeImage && (
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    background: "#d4edda",
                    border: "1px solid #c3e6cb",
                    color: "#155724",
                    borderRadius: "6px",
                    padding: "6px 12px",
                    fontSize: "0.85rem",
                    fontWeight: 600,
                    margin: "8px 0"
                  }}>
                    ✓ Image uploaded &amp; inserted successfully!
                  </div>
                )}

                {/* Error message */}
                {codeImageError && (
                  <div className="text-danger small" style={{ margin: "8px 0" }}>
                    ⚠️ {codeImageError}
                  </div>
                )}

                {/* Grid of uploaded images to insert */}
                {uploadedCodeImages.length > 0 && (
                  <div style={{ marginTop: "0.75rem" }}>
                    <div className="text-muted small" style={{ marginBottom: "0.5rem" }}>
                      Click any image to insert it at your cursor position:
                    </div>
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(60px, 1fr))",
                      gap: "8px"
                    }}>
                      {uploadedCodeImages.map((url, idx) => (
                        <div
                          key={idx}
                          onClick={() => insertCodeImageAtCursor(url)}
                          style={{
                            position: "relative",
                            aspectRatio: "1",
                            border: "1px solid #dee2e6",
                            borderRadius: "4px",
                            overflow: "hidden",
                            cursor: "pointer"
                          }}
                          title="Click to insert at cursor"
                        >
                          <img
                            src={url}
                            alt={`Uploaded ${idx}`}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                          <div style={{
                            position: "absolute",
                            inset: 0,
                            background: "rgba(0, 0, 0, 0.4)",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            opacity: 0,
                            transition: "opacity 0.2s",
                            fontSize: "0.7rem",
                            fontWeight: "600"
                          }}
                            onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
                            onMouseLeave={(e) => e.currentTarget.style.opacity = 0}
                          >
                            Insert
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Detailed Question Content - Always visible */}
              <div className={styles.sectionDivider}>
                <span>Question Details & Code</span>
              </div>

              {/* Initial Code */}
              <div className={styles.formGroup}>
                <label>Initial Code Template</label>
                <textarea
                  name="initialCode"
                  value={codeQuestionForm.initialCode}
                  onChange={handleCodeQuestionInputChange}
                  rows={6}
                  className={styles.formTextarea}
                  style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
                  placeholder="// Starter code for students..."
                />
              </div>

              {/* Hints */}
              <div className={styles.formGroup}>
                <label>Hints</label>
                {codeQuestionForm.hints.map((hint, index) => (
                  <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <input
                      type="text"
                      value={hint}
                      onChange={(e) => updateHint(index, e.target.value)}
                      placeholder={`Hint ${index + 1}`}
                      className={styles.formInput}
                    />
                    {codeQuestionForm.hints.length > 1 && (
                      <button
                        type="button"
                        className={styles.secondaryBtn}
                        onClick={() => removeHint(index)}
                        style={{ padding: '0.5rem' }}
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  className={styles.secondaryBtn}
                  onClick={addHint}
                  style={{ marginTop: '0.5rem' }}
                >
                  <Plus size={14} /> Add Hint
                </button>
              </div>

              {/* Solution */}
              <div className={styles.formGroup}>
                <label>Solution Code</label>
                <textarea
                  name="solution"
                  value={codeQuestionForm.solution}
                  onChange={handleCodeQuestionInputChange}
                  rows={8}
                  className={styles.formTextarea}
                  style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
                  placeholder="// Complete solution..."
                />
              </div>

              {/* Test Cases */}
              <div className={styles.formGroup}>
                <label>Test Cases</label>
                <textarea
                  name="testCases"
                  value={codeQuestionForm.testCases}
                  onChange={handleCodeQuestionInputChange}
                  rows={4}
                  className={styles.formTextarea}
                  placeholder="Describe test cases..."
                />
              </div>

              {/* Publish & Proctored */}
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      name="isProctored"
                      checked={codeQuestionForm.isProctored}
                      onChange={handleCodeQuestionInputChange}
                      className={styles.checkbox}
                    />
                    <span>Proctored Test</span>
                  </label>
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      name="isPublished"
                      checked={codeQuestionForm.isPublished}
                      onChange={handleCodeQuestionInputChange}
                      className={styles.checkbox}
                    />
                    <span>Publish Test</span>
                  </label>
                </div>
              </div>

              {/* Actions */}
              <div className={styles.modalActions}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCodeQuestionModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  <Save size={14} className="me-1" />
                  {editingCodeQuestion ? "Update" : "Create"} Code Question
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CODE QUESTION BULK UPLOAD MODAL */}
      {showCodeQuestionBulkUploadModal && (
        <div className={styles.modalOverlay} onClick={() => {
          if (!codeQuestionZipImporting) {
            setShowCodeQuestionBulkUploadModal(false);
          }
        }}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h5>Bulk Upload Code Questions</h5>
              <button
                type="button"
                className={styles.closeBtn}
                onClick={() => setShowCodeQuestionBulkUploadModal(false)}
                disabled={codeQuestionZipImporting}
              >
                <X size={18} />
              </button>
            </div>

            <div className={styles.modalBody}>
              {/* Tab Navigation */}
              <div className={styles.tabRow} style={{ marginBottom: '1.25rem', borderBottom: '1px solid #e9ecef', paddingBottom: '0.5rem' }}>
                <button
                  type="button"
                  className={`${styles.tabButton} ${codeQuestionBulkUploadType === "csv" ? styles.tabButtonActive : ""}`}
                  onClick={() => {
                    setCodeQuestionBulkUploadType("csv");
                    setCsvFile(null);
                    setCsvUploadStatus({ message: "", type: "" });
                  }}
                  style={{ borderRadius: '4px', fontSize: '0.85rem' }}
                  disabled={codeQuestionZipImporting}
                >
                  <FileText size={14} />
                  CSV Upload
                </button>
                <button
                  type="button"
                  className={`${styles.tabButton} ${codeQuestionBulkUploadType === "zip" ? styles.tabButtonActive : ""}`}
                  onClick={() => {
                    setCodeQuestionBulkUploadType("zip");
                    setCodeQuestionZipFile(null);
                    setCodeQuestionZipStatus({ message: "", type: "", details: [] });
                  }}
                  style={{ borderRadius: '4px', fontSize: '0.85rem' }}
                  disabled={codeQuestionZipImporting}
                >
                  <Upload size={14} />
                  ZIP Bundle (with Images)
                </button>
              </div>

              {codeQuestionBulkUploadType === "csv" ? (
                // Original CSV Upload Panel
                <div className={styles.formGroup}>
                  <p>Upload a CSV file containing multiple coding tests. Use the template to ensure correct headers.</p>
                  <button
                    type="button"
                    className="btn btn-outline-primary me-2 mb-2"
                    onClick={downloadCodeQuestionCSVTemplate}
                  >
                    <Download size={16} className="me-2" />
                    Download Template
                  </button>
                  <div className="mb-3">
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleCSVUpload}
                      className="form-control"
                    />
                  </div>

                  {csvFile && (
                    <div className="alert alert-info">
                      <strong>Selected:</strong> {csvFile.name}
                    </div>
                  )}

                  {csvUploadStatus.message && (
                    <div className={`alert alert-${csvUploadStatus.type === 'error' ? 'danger' : csvUploadStatus.type === 'info' ? 'info' : 'success'}`}>
                      {csvUploadStatus.message}
                      {csvUploadStatus.errors && csvUploadStatus.errors.length > 0 && (
                        <details className="mt-2">
                          <summary style={{ cursor: 'pointer' }}>View errors ({csvUploadStatus.errors.length})</summary>
                          <ul className="mt-2 mb-0">
                            {csvUploadStatus.errors.map((err, idx) => (
                              <li key={idx}>{err}</li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </div>
                  )}

                  <div className={styles.modalActions}>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowCodeQuestionBulkUploadModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={processBulkCodeQuestionCSV}
                      disabled={!csvFile}
                    >
                      <Upload size={16} className="me-2" />
                      Import Questions
                    </button>
                  </div>
                </div>
              ) : (
                // New ZIP Upload Panel
                <div className={styles.csvUploadCard} style={{ background: '#fbfcfe', border: '1px solid #d0e0fc', padding: '1.25rem', borderRadius: '0.75rem' }}>
                  <div className={styles.csvUploadHeader} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h6 style={{ color: '#1e3a8a', display: 'flex', alignItems: 'center', gap: '6px', margin: 0 }}>
                      <span style={{ fontSize: '1.1rem' }}>📦</span>
                      Import Code Questions + Images (ZIP Bundle)
                    </h6>
                    <button
                      type="button"
                      className={styles.downloadTemplateBtn}
                      onClick={downloadCodeQuestionZipTemplate}
                      style={{ background: '#3b82f6', color: '#fff', border: 'none', padding: '0.35rem 0.7rem', borderRadius: '0.5rem', cursor: 'pointer' }}
                      disabled={codeQuestionZipImporting}
                    >
                      <Download size={14} className="me-1" />
                      Template ZIP
                    </button>
                  </div>
                  <div className={styles.csvInstructions} style={{ background: '#eff6ff', color: '#1e40af', padding: '0.75rem', borderRadius: '0.5rem', marginBottom: '1rem', display: 'flex', gap: '0.75rem', borderLeft: '3px solid #0d6efd' }}>
                    <AlertCircle size={16} style={{ flexShrink: 0 }} />
                    <p style={{ margin: 0, fontSize: '0.8rem', lineHeight: '1.5' }}>
                      Upload a ZIP file containing: 1) <strong>code_questions.csv</strong> with coding problems referencing <code>{"{{img:image_name.png}}"}</code> placeholders inside the Description, and 2) an <strong>images/</strong> folder containing the corresponding image files.
                    </p>
                  </div>
                  <div className={styles.csvUploadArea} style={{ background: '#f3f4f6', border: '2px dashed #9ca3af', padding: '1.5rem', borderRadius: '0.5rem', textAlign: 'center', position: 'relative', marginBottom: '1rem' }}>
                    <input
                      type="file"
                      accept=".zip"
                      onChange={handleCodeQuestionZipFileChange}
                      className={styles.csvFileInput}
                      id="codeQuestionZipFileInput"
                      disabled={codeQuestionZipImporting}
                      style={{ position: 'absolute', opacity: 0, inset: 0, cursor: 'pointer', width: '100%', height: '100%' }}
                    />
                    <label htmlFor="codeQuestionZipFileInput" className={styles.csvFileLabel} style={{ color: '#4b5563', cursor: 'pointer', display: 'block', margin: 0 }}>
                      {codeQuestionZipFile ? codeQuestionZipFile.name : "Choose ZIP bundle or drag here"}
                    </label>
                    {codeQuestionZipFile && (
                      <small style={{ display: 'block', color: '#6b7280', marginTop: '4px' }}>
                        Size: {(codeQuestionZipFile.size / 1024).toFixed(1)} KB
                      </small>
                    )}
                  </div>

                  {/* Progress Bar */}
                  {codeQuestionZipImporting && (
                    <div style={{ padding: '1rem 0' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <div className="spinner-border spinner-border-sm text-primary" role="status" />
                        <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#374151' }}>
                          {codeQuestionZipProgress.phase}
                          {codeQuestionZipProgress.total > 0 && ` (${codeQuestionZipProgress.current}/${codeQuestionZipProgress.total})`}
                        </span>
                      </div>
                      {codeQuestionZipProgress.total > 0 && (
                        <div style={{ background: '#e5e7eb', height: '8px', borderRadius: '4px', overflow: 'hidden' }}>
                          <div
                            style={{
                              background: '#3b82f6',
                              width: `${Math.round((codeQuestionZipProgress.current / codeQuestionZipProgress.total) * 100)}%`,
                              height: '100%'
                            }}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Status Message */}
                  {codeQuestionZipStatus.message && (
                    <div className={`alert alert-${codeQuestionZipStatus.type === "success" ? "success"
                        : codeQuestionZipStatus.type === "warning" ? "warning"
                          : "danger"
                      } alert-dismissible fade show`} style={{ margin: '1rem 0' }}>
                      {codeQuestionZipStatus.message}
                      {codeQuestionZipStatus.details.length > 0 && (
                        <details style={{ marginTop: "0.5rem" }}>
                          <summary style={{ cursor: 'pointer', fontWeight: 600 }}>View details ({codeQuestionZipStatus.details.length})</summary>
                          <ul style={{ marginBottom: 0, marginTop: "0.5rem", maxHeight: '150px', overflowY: 'auto' }}>
                            {codeQuestionZipStatus.details.map((d, idx) => (
                              <li key={idx} style={{ fontSize: '0.85rem' }}>{d}</li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </div>
                  )}

                  <div className={styles.csvUploadActions} style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={codeQuestionZipImporting}
                      onClick={() => {
                        setShowCodeQuestionBulkUploadModal(false);
                        setCodeQuestionZipFile(null);
                        setCodeQuestionZipStatus({ message: "", type: "", details: [] });
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={processCodeQuestionZipBundle}
                      disabled={!codeQuestionZipFile || codeQuestionZipImporting}
                      style={{ background: '#2563eb', borderColor: '#2563eb' }}
                    >
                      {codeQuestionZipImporting ? "Importing..." : "Import ZIP Bundle"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}


      {/* ========== RESULTS TAB ========== */}
      {
        activeSubTab === "results" && (
          <div className={styles.card}>
            <h5 className={styles.sectionTitle}>Test Results</h5>
            <p className={styles.helperText}>
              View and filter test performance. These records come from the{" "}
              <code>testResults</code> collection whenever a student finishes a test.
            </p>

            <div className={styles.filters}>
              <select
                value={resultsFilter}
                onChange={(e) => setResultsFilter(e.target.value)}
                className={styles.filterSelect}
              >
                <option value="all">All Results</option>
                <option value="passed">Passed</option>
                <option value="failed">Failed</option>
              </select>
            </div>

            {filteredResults.length === 0 ? (
              <div className={styles.emptyState}>
                <p>No test results found.</p>
              </div>
            ) : (
              <div className={styles.tableWrapper}>
                <table className={styles.resultsTable}>
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Test</th>
                      <th>Score</th>
                      <th>Pass Marks</th>
                      <th>Status</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredResults.map((r) => (
                      <tr key={r.id}>
                        <td>{r.userName || r.userId}</td>
                        <td>{r.testTitle || r.testId}</td>
                        <td>{r.score}</td>
                        <td>{r.passMarks}</td>
                        <td>
                          {r.score >= r.passMarks ? (
                            <span className="badge bg-success">Passed</span>
                          ) : (
                            <span className="badge bg-danger">Failed</span>
                          )}
                        </td>
                        <td>
                          {r.completedAt
                            ? new Date(r.completedAt.seconds * 1000).toLocaleString()
                            : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )
      }
    </div >
  );
}

export default TestManagement;


